<script setup>
// hls.js loaded dynamically — keeps it out of the entry bundle
let Hls = null
// anime4k.js loaded dynamically too — тільки якщо юзер реально вмикає шейдер
let Anime4KJS = null

const { find } = useStrapi()
const user = useStrapiUser()
// Токен — для історії переглядів у профілі (див. syncProgress)
const accountToken = useStrapiToken()
const router = useRouter()
const route = useRoute()
const config = useRuntimeConfig()

// HLS Cache Service Worker
const {
  isSupported: hlsCacheSupported,
  register: registerHLSCache,
  setEpisodeContext,
  downloadEpisode: downloadEpisodeSW,
  downloadProgress,
  getEpisodeStats
} = useHLSCache()

// Стан завантаження поточного епізоду
const isDownloading = ref(false)
const isDownloaded = ref(false)
const fisrtPlay = ref(false)

const props = defineProps({
  anime: {
    type: Object,
    default: () => ({ episodes: [] })
  },
  // Запис ефіру з архіву (pages/archive/[id].vue): { id }. Той самий плеєр,
  // але без усього, що прив'язане до аніме: історії переглядів у профілі,
  // офлайн-завантажень, кліпів, гілок серій і чату сторінки аніме. Сам
  // запис приходить як «аніме» з однією серією
  archive: {
    type: Object,
    default: null
  }
})

const isArchive = computed(() => !!props.archive)
// Ключ, під яким пам'ятаємо місце перегляду в localStorage. Для архіву —
// з двокрапкою: профіль переносить у серверну історію всі ключі-слуги
// аніме (pages/profile.vue → importLocalHistory), і запис ефіру там
// виглядав би як аніме «archive-2»
const progressKey = computed(() =>
  props.archive ? `archive:${props.archive.id}` : route.params.name
)

// --- Refs & State ---
// Используем ref вместо useState для локального состояния
const failed = ref(false) // Исправлено failded -> failed
const current = ref(0)
// Публикуємо поточний епізод для лічильника живого онлайну (useOnlinePresence)
const watchCurrentEpisode = useState('watchCurrentEpisode', () => null)
watch(current, (val) => (watchCurrentEpisode.value = val), { immediate: true })
// Контекст для плаваючого чату: звідси він знає, що можна відкрити гілку
// аніме або конкретної серії, а не тільки загальну
const {
  setChatContext,
  clearChatContext,
  chatConfig: siteChatConfig,
  scopes: chatScopes,
  isChosen: chatScopeChosen,
  selectScope: selectChatScope,
  playerChatRequest
} = useSiteChat()
watchEffect(() => {
  if (props.archive) return clearChatContext()
  setChatContext({
    slug: route.params.name,
    title: props.anime?.title || route.params.name,
    episodeIndex: current.value,
    episodeTitle:
      props.anime?.episodes?.[current.value]?.title || `Серія ${current.value + 1}`
  })
})
onBeforeUnmount(clearChatContext)
const onlineStats = useState('onlinePresenceStats', () => ({
  global: 0,
  anime: null,
  episode: null,
  episodes: null
}))
const isMobile = ref(false)
const loop = ref(false)
const chatShow = ref(false)
const chatSize = ref(20)
const chatHeight = ref(20)
const chatOpacity = ref(50)
// Режим показу чату: 'theater' — панель праворуч від відео, 'popup' — окреме
// вікно поверх (для чату сайту це те саме плаваюче вікно, що й поза плеєром)
const chatMode = ref('theater')
const chatAvailable = ref(false)
// Що показувати: чат сайту (гілка поточної сторінки) або архівний чат стріму,
// синхронний з таймкодом серії. За замовчуванням — архів серії: людина
// прийшла дивитись конкретний епізод.
const chatSource = ref('archive')
// Чи вибирав користувач джерело вручну. Поки ні — самі підставляємо архів
// там, де він є, і чат сайту там, де архіву немає.
const chatSourceChosen = ref(false)

// Архіву для цієї серії немає (або він не завантажився): якщо джерело
// вибирав користувач — просто гасимо панель, інакше тихо йдемо в чат сайту
const dropArchiveSource = () => {
  if (!chatSourceChosen.value) chatSource.value = 'site'
  else if (chatSource.value === 'archive') chatShow.value = false
}
// Спільний з TwitchChatWidget стан плаваючого чату Twitch
const twitchChatOpen = useState('twitchChatOpen', () => false)
const heroku = ref(false)
const savedVideoSpeed = ref(1)

// --- Anime4K шейдер (апскейл) ---
const shaderSupported = ref(false) // Дешева перевірка WebGL, без завантаження бібліотеки
const shaderMode = ref('off') // 'off' | 'simple' | 'lower-a' | 'lower-b' | 'lower-c' | 'higher-a' | 'higher-b' | 'higher-c'
const shaderQuality = ref('m') // залежить від shaderMode: S/M/L/VL/UL для simple, normal/fast для lower-*/higher-*
const shaderActive = computed(
  () => shaderMode.value !== 'off' && shaderSupported.value
)
const videoUpscaler = shallowRef(null)

// Підписи чесно показують, у скільки разів реально виросте картинка —
// це саме те, що ці пресети роблять "самі по собі", без жодних підмін
const shaderModeOptions = [
  { value: 'off', label: 'Вимкнено' },
  { value: 'restore', label: 'Restore (без апскейлу)' },
  { value: 'simple', label: 'Simple (апскейл x2)' },
  { value: 'lower-a', label: 'Lower-End A (апскейл x4)' },
  { value: 'lower-b', label: 'Lower-End B (апскейл x4)' },
  { value: 'lower-c', label: 'Lower-End C (апскейл x4)' },
  { value: 'higher-a', label: 'Higher-End A (апскейл x4)' },
  { value: 'higher-b', label: 'Higher-End B (апскейл x4)' },
  { value: 'higher-c', label: 'Higher-End C (апскейл x4)' }
]

const shaderQualityOptions = computed(() => {
  if (shaderMode.value === 'simple' || shaderMode.value === 'restore') {
    return [
      { value: 's', label: 'Швидкий' },
      { value: 'm', label: 'Середній' },
      { value: 'l', label: 'Великий' },
      { value: 'vl', label: 'Дуже великий' },
      { value: 'ul', label: 'Ультравеликий' }
    ]
  }
  if (shaderMode.value === 'off') return []
  return [
    { value: 'normal', label: 'Звичайна' },
    { value: 'fast', label: 'Швидка' }
  ]
})

const defaultShaderQuality = (mode) => {
  if (mode === 'simple' || mode === 'restore') return 'm'
  if (mode === 'off') return null
  return 'normal'
}

// Роздільність виводу:
// - native — без CNN-апскейлу, лише реставрація на нативній роздільності відео
// - 2k — та сама дешева нативна обробка, але показ розтягнутий до ~1440p
//   (без додаткового CNN-проходу, тому не важче за native)
// - 4k — повний CNN-апскейл x2 (справжні 2160p при джерелі 1080p)
const shaderScale = ref('2k')
const shaderScaleOptions = [
  { value: 'native', label: 'Нативна' },
  { value: '2k', label: '2K' },
  { value: '4k', label: '4K' }
]

// --- Режим порівняння (split-screen: оригінал | апскейл) ---
const compareMode = ref(false)
const compareDividerPct = ref(50) // 0–100, позиція розділювача у відсотках від ширини
const isDraggingDivider = ref(false)
const compareContainerRef = ref(null)

// --- localStorage persistence для налаштувань чату ---
const CHAT_STORAGE_KEY = 'chat_settings'

const loadChatSettings = () => {
  if (!import.meta.client) return
  try {
    const saved = JSON.parse(localStorage.getItem(CHAT_STORAGE_KEY))
    if (!saved) return
    if (saved.show !== undefined) chatShow.value = saved.show
    if (saved.size !== undefined) chatSize.value = saved.size
    if (saved.height !== undefined) chatHeight.value = saved.height
    if (saved.opacity !== undefined) chatOpacity.value = saved.opacity
    // pip — ключ старої версії, зараз це режим 'popup'
    if (saved.mode !== undefined) chatMode.value = saved.mode
    else if (saved.pip !== undefined) {
      chatMode.value = saved.pip ? 'popup' : 'theater'
    }
    // Чат Twitch з панелі прибрали — старий вибір переводимо на чат сайту.
    // Поважаємо лише свідомий вибір користувача: без прапорця sourceChosen
    // (у т.ч. у старих збереженнях) лишаємо автопідстановку архіву.
    if (saved.source !== undefined && saved.sourceChosen) {
      chatSource.value = saved.source === 'archive' ? 'archive' : 'site'
      chatSourceChosen.value = true
    }
  } catch (e) {}
}

const saveChatSettings = () => {
  if (!import.meta.client) return
  try {
    localStorage.setItem(
      CHAT_STORAGE_KEY,
      JSON.stringify({
        show: chatShow.value,
        size: chatSize.value,
        height: chatHeight.value,
        opacity: chatOpacity.value,
        mode: chatMode.value,
        source: chatSource.value,
        sourceChosen: chatSourceChosen.value
      })
    )
  } catch (e) {}
}

// --- localStorage persistence для налаштувань апскейлу ---
const SHADER_STORAGE_KEY = 'anime4k_settings'

const loadShaderSettings = () => {
  if (!import.meta.client) return
  try {
    const saved = JSON.parse(localStorage.getItem(SHADER_STORAGE_KEY))
    if (!saved) return
    if (saved.mode !== undefined) shaderMode.value = saved.mode
    if (saved.quality !== undefined) shaderQuality.value = saved.quality
    if (saved.scale !== undefined) shaderScale.value = saved.scale
    if (saved.compareMode !== undefined) compareMode.value = saved.compareMode
  } catch (e) {}
}

const saveShaderSettings = () => {
  if (!import.meta.client) return
  try {
    localStorage.setItem(
      SHADER_STORAGE_KEY,
      JSON.stringify({
        mode: shaderMode.value,
        quality: shaderQuality.value,
        scale: shaderScale.value,
        compareMode: compareMode.value
      })
    )
  } catch (e) {}
}

// --- Автоперемикання серії ---
// Вмикається за замовчуванням: така поведінка була до появи перемикача
const PLAYER_STORAGE_KEY = 'player_settings'
const autoNextEpisode = ref(true)

const loadPlayerSettings = () => {
  if (!import.meta.client) return
  try {
    const saved = JSON.parse(localStorage.getItem(PLAYER_STORAGE_KEY))
    if (!saved) return
    if (saved.autoNextEpisode !== undefined)
      autoNextEpisode.value = saved.autoNextEpisode
  } catch (e) {}
}

const savePlayerSettings = () => {
  if (!import.meta.client) return
  try {
    localStorage.setItem(
      PLAYER_STORAGE_KEY,
      JSON.stringify({ autoNextEpisode: autoNextEpisode.value })
    )
  } catch (e) {}
}

const onEpisodeEnded = () => {
  if (!autoNextEpisode.value) return
  const next = current.value + 1
  if (next >= (props.anime?.episodes?.length || 0)) return
  changeEpisode(next, true)
}

// --- Дебаг-інформація ---
const showDebugInfo = ref(false)
const debugStats = ref(null)
let debugInterval = null

// Ступені CNN-апскейлу x2. У готових пресетів Lower/Higher-End їх аж ДВІ
// (сумарно апскейл x4 = 8K на 1080p джерелі!) — тому не просто вирізаємо
// їх усі чи лишаємо всі, а обмежуємо кількість, яку лишити в конфігу
const UPSCALE_STAGE_NAMES = [
  'Anime4K_Upscale_CNN_x2_S',
  'Anime4K_Upscale_CNN_x2_M',
  'Anime4K_Upscale_CNN_x2_L',
  'Anime4K_Upscale_CNN_x2_VL',
  'Anime4K_Upscale_CNN_x2_UL',
  'Anime4K_Upscale_Denoise_CNN_x2_S',
  'Anime4K_Upscale_Denoise_CNN_x2_M',
  'Anime4K_Upscale_Denoise_CNN_x2_L',
  'Anime4K_Upscale_Denoise_CNN_x2_VL',
  'Anime4K_Upscale_Denoise_CNN_x2_UL'
]

// Лишає тільки останні `keep` ступенів апскейлу x2 у конфігу (0 — прибрати
// геть усі, 1 — гарантовано рівно один апскейл x2, скільки б їх не було
// у вихідному пресеті)
const capUpscaleStages = (config, keep) => {
  const upscaleClasses = new Set(
    UPSCALE_STAGE_NAMES.map((name) => Anime4KJS[name]).filter(Boolean)
  )
  const total = config.filter((ctor) => upscaleClasses.has(ctor)).length
  let seen = 0
  return config.filter((ctor) => {
    if (!upscaleClasses.has(ctor)) return true
    seen++
    return seen > total - keep
  })
}

// Елементи DOM
const videoRef = ref(null)
const shaderCanvasRef = ref(null) // прихований канвас, куди рендерить сама бібліотека
const shaderDisplayCanvasRef = ref(null) // видимий канвас — показуємо його користувачу
const playerRef = ref(null)
const chatContainerRef = ref(null)
const chatElRef = ref(null) // Для draggable
const chatPipRef = ref(null)
const episodesContainerRef = ref(null)
const canScrollEpisodesUp = ref(false)
const canScrollEpisodesDown = ref(false)
let episodesResizeObserver = null

// HLS Instance
const hlsInstance = shallowRef(null)

// --- Utils Helpers ---
const getRandomColor = () => {
  const letters = '0123456789ABCDEF'
  let color = '#'
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)]
  }
  return color
}

const formatDuration = (seconds) => {
  if (!seconds) return '00:00'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const mDisplay = m < 10 ? `0${m}` : m
  const sDisplay = s < 10 ? `0${s}` : s
  return h > 0 ? `${h}:${mDisplay}:${sDisplay}` : `${mDisplay}:${sDisplay}`
}

// --- Composables ---
const controls = useMediaControls(videoRef)
const {
  playing,
  buffered,
  currentTime,
  duration,
  waiting,
  volume,
  muted
} = controls

// Картинка в картинці — своя, а не з useMediaControls: та знає лише
// стандартний API, якого на iPhone немає, і пункт меню там просто зникав
// (див. utils/pip.js). Перевіряємо, коли в <video> вже є кадр
const supportsPictureInPicture = ref(false)
const detectPipSupport = () => {
  supportsPictureInPicture.value = canUsePip(videoRef.value)
}
const togglePictureInPicture = () => switchPip(videoRef.value)


// Кнопка чату у фулскріні зникає разом з керуванням плеєра — тому віддаємо
// його видимість назовні, у плаваючий віджет
const playerControlsVisible = useState('playerControlsVisible', () => false)

// --- Панель керування: коли показувати ---
// Як у плеєрі shiki.sgezha.art: панель зʼявляється від руху миші чи дотику і
// ховається за 2,8 с, поки серія йде. На паузі — видна завжди. Поки відкрите
// якесь меню панелі, не ховаємо: на телефоні вона інакше їхала разом із
// меню посеред вибору
const CONTROLS_HIDE_MS = 2800
const controlsVisible = ref(true)
const chatMenuRef = ref(null)
const shaderMenuRef = ref(null)
const settingsMenuRef = ref(null)
let controlsHideTimer = null

const anyMenuOpen = () =>
  [chatMenuRef, shaderMenuRef, settingsMenuRef].some((menu) => menu.value?.isOpen)

const showControls = () => {
  controlsVisible.value = true
  if (controlsHideTimer) clearTimeout(controlsHideTimer)
  controlsHideTimer = setTimeout(() => {
    if (anyMenuOpen()) showControls()
    else if (playing.value) controlsVisible.value = false
  }, CONTROLS_HIDE_MS)
}

const hideControls = () => {
  if (controlsHideTimer) clearTimeout(controlsHideTimer)
  if (anyMenuOpen()) return
  controlsVisible.value = false
}

// Миша пішла з плеєра — не чекаємо таймера, як і раніше
const onPlayerMouseleave = () => {
  if (playing.value) hideControls()
}

watch(playing, (isPlaying) => {
  // Серію запустили не тапом по заставці (кнопкою, продовженням,
  // автопереходом) — заставка з «грати» більше не потрібна. Інакше вона
  // лишалась лежати поверх кадру й ковтала тапи, тож жести до відео не доходили
  if (isPlaying) fisrtPlay.value = true
  if (isPlaying) showControls()
  else {
    if (controlsHideTimer) clearTimeout(controlsHideTimer)
    controlsVisible.value = true
  }
})

onBeforeUnmount(() => {
  if (controlsHideTimer) clearTimeout(controlsHideTimer)
})

watchEffect(() => (playerControlsVisible.value = controlsVisible.value))
onBeforeUnmount(() => (playerControlsVisible.value = false))
const {
  isSupported: orientSupported,
  lockOrientation,
  unlockOrientation
} = useScreenOrientation()

// Draggable чат (PIP режим)
const {
  x,
  y,
  style: draggableStyle
} = useDraggable(chatElRef, {
  initialValue: { x: 20, y: 20 }
})

// --- Fullscreen Logic ---
const isFullscreen = ref(false)

const handleFullscreenChange = () => {
  const doc = document
  isFullscreen.value = !!(
    doc.fullscreenElement ||
    doc.webkitFullscreenElement ||
    doc.mozFullScreenElement ||
    doc.msFullscreenElement
  )
}

// iPhone не вміє фулскрін для довільного елемента: у Safari на ньому немає ні
// requestFullscreen, ні webkitRequestFullscreen — і це не тимчасово, WebKit
// тримає це відкритим запитом уже шостий рік (bugs.webkit.org 206854), а
// префіксний шлях закрив як WONTFIX. На iPad і всюди інде звичайний API є.
//
// Єдине, що там працює, — нативний плеєр самого <video>. Наших контролів і
// чату в ньому не буде, керує ним iOS, зате це справжній фулскрін із поворотом
// екрана. Кращого варіанта на цій платформі просто немає: розтягнути сторінку
// на весь екран через CSS не сховає ні адресний рядок, ні панель Safari.
const enterNativeVideoFullscreen = () => {
  const video = videoRef.value
  if (!video || typeof video.webkitEnterFullscreen !== 'function') return false
  // Поки не приїхали метадані, відео ще не знає, чи вміє фулскрін
  if (video.webkitSupportsFullscreen === false) return false

  video.webkitEnterFullscreen()
  return true
}

const toggleFullScreen = async () => {
  if (!playerRef.value) return

  if (!isFullscreen.value) {
    const el = playerRef.value
    try {
      if (el.requestFullscreen) await el.requestFullscreen()
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen()
      else if (el.msRequestFullscreen) await el.msRequestFullscreen()
      // Нативний плеєр живе сам по собі: подій документа він не шле, і стан
      // сторінки під ним чіпати не треба — вийде людина тією ж кнопкою iOS
      else if (enterNativeVideoFullscreen()) return

      if (orientSupported.value) {
        try {
          await lockOrientation('landscape-primary')
        } catch (e) {}
      }
    } catch (err) {
      // API є, але не дав дозволу — лишається розтягнути плеєр стилями
      if (enterNativeVideoFullscreen()) return
      isFullscreen.value = true
      console.error('Fullscreen error:', err)
    }
  } else {
    try {
      const doc = document
      if (doc.exitFullscreen) await doc.exitFullscreen()
      else if (doc.webkitExitFullscreen) await doc.webkitExitFullscreen()
      else if (typeof videoRef.value?.webkitExitFullscreen === 'function') {
        videoRef.value.webkitExitFullscreen()
      }

      if (orientSupported.value) {
        unlockOrientation()
      }
    } catch (err) {
      isFullscreen.value = false
      console.error('Exit fullscreen error:', err)
    }
  }
}

watch(isFullscreen, (val) => {
  if (import.meta.client) {
    document.body.style.overflow = val ? 'hidden' : ''
    // ЕСЛИ выходим из полноэкранного режима, возвращаем курсор
    if (!val) {
      document.body.style.cursor = 'default'
    }
  }
})

// --- HLS Config ---
// Єдине місце, де ми лишаємося на нативному HLS браузера, — справжній WebKit:
// iPhone, iPad та Safari на маку. Там нативний шлях давно вилизаний і
// стабільніший за MSE. Усе інше — Android, Chrome (у тому числі на маку),
// Firefox, Edge — грає через hls.js.
//
// Перевіряти саме браузер, а не платформу, тут принципово: Chrome на маку теж
// має в UA "Macintosh" і на canPlayType('application/vnd.apple.mpegurl')
// відповідає "maybe", тобто за платформною перевіркою мовчки з'їжджав на
// нативний шлях. Так само Chrome на Android рапортує "maybe", а грає HLS через
// Android-бекенд, який тягне сегменти Range-запитами і тримає буфер приблизно
// в один сегмент — керувати цим з JS неможливо.
//
// iPadOS з 13-ї версії прикидається десктопним маком, тому окремо ловимо його
// за наявністю мультитача: у справжніх маків maxTouchPoints — 0.
const isWebKitBrowser = () => {
  if (!import.meta.client) return false
  const ua = navigator.userAgent
  // Chrome/Edge/Firefox на iOS — це той самий WebKit, лише з іншим ім'ям
  if (/iPhone|iPad|iPod/.test(ua)) return true
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return true // iPadOS
  // Safari на маку: єдиний, хто має Safari/ і не має жодного з чужих маркерів
  return (
    /Safari\//.test(ua) && !/Chrome\/|Chromium\/|Edg\/|OPR\/|Firefox\//.test(ua)
  )
}

// Мобільні браузери мають жорсткий ліміт пам'яті на SourceBuffer (~12-30 МБ).
// Якщо просити в них великий буфер, MSE кидає QuotaExceededError, а hls.js у
// відповідь назавжди ріже maxMaxBufferLength навпіл (buffer-controller ->
// reduceMaxBufferLength) — і буфер осідає на ~10с. Тому на мобільних просимо
// помірний буфер і активно чистимо те, що вже переглянуто (backBufferLength).
const hlsConfig = computed(() => ({
  maxBufferLength: isMobile.value ? 300 : 300,
  maxMaxBufferLength: isMobile.value ? 600 : 600,
  maxBufferSize: (isMobile.value ? 500 : 500) * 1000 * 1000,
  backBufferLength: isMobile.value ? 300 : Infinity,
  progressive: false,
  autoStartLoad: true,
  startFragPrefetch: true,
  enableWorker: true,
  fragLoadingMaxRetry: 10,
  fragLoadingRetryDelay: 1000,
  manifestLoadingMaxRetry: 5,
  levelLoadingMaxRetry: 5,
  lowLatencyMode: false,
  xhrSetup: (xhr, url) => {
    if (props?.anime?.privat && import.meta.client && window.videoToken) {
      url = url + `?t=${window.videoToken}`
      xhr.open('GET', url, true)
    }
  },
  fetchSetup: (context, initParams) => {
    let url = context.url

    if (props?.anime?.privat && import.meta.client && window.videoToken) {
      url = url + `?t=${window.videoToken}`
    }

    return new Request(url, initParams)
  }
}))

// --- Lifecycle & Init ---

// Запит токена для приватних аніме
const requestVideoToken = async () => {
  if (!import.meta.client) return
  if (!user?.value?.privat || !props?.anime?.privat) return

  try {
    // console.log('[Player] Requesting video token for private anime')
    const videoTokenData = await find('video-token')
    // console.log('[Player] Video token response:', videoTokenData)

    if (videoTokenData) {
      window.videoToken = videoTokenData?.data?.attributes?.token
      // console.log('[Player] Video token set:', window.videoToken ? 'YES' : 'NO')
    }
  } catch (er) {
    console.error('[Player] Video token error:', er)
  }
}

// Watch для запиту токена коли аніме завантажується
watch(
  () => props.anime?.privat,
  (isPrivat) => {
    if (isPrivat && user?.value?.privat) {
      requestVideoToken()
    }
  },
  { immediate: true }
)

onMounted(async () => {
  if (import.meta.client) {
    // Load hls.js dynamically — not part of the entry bundle
    const hlsModule = await import('hls.js')
    Hls = hlsModule.default
    // Реєструємо HLS Service Worker
    if (hlsCacheSupported.value && !props.archive) {
      try {
        await registerHLSCache()
        // console.log('[Player] HLS cache enabled')
        // Завантажуємо статус кешу для всіх серій
        await loadAllEpisodesCache()
      } catch (error) {
        console.warn('[Player] HLS cache registration failed:', error)
      }
    }

    if (user?.value?.privat && props?.anime?.privat) {
      await requestVideoToken()
    } else {
      // console.log('[Player] Skipping video token in onMounted:', {
      //   userPrivat: user?.value?.privat,
      //   animePrivat: props?.anime?.privat
      // })
    }

    if (window.innerWidth < 1024) isMobile.value = true
    window.addEventListener('keydown', keyHandler)
    window.addEventListener('resize', updateEpisodesScrollState)

    // Дешева перевірка підтримки WebGL для шейдера (без завантаження anime4k.js).
    // Anime4K тепер і на телефонах — там це єдиний рушій апскейлу (NijiLucid
    // на дотикових пристроях вимкнений, див. nijiSupported). Чи потягне
    // залізо, вирішує сама перевірка WebGL-розширень, а не ширина екрана
    shaderSupported.value = checkShaderSupport()

    // Відновлюємо збережені налаштування шейдера
    loadShaderSettings()

    // Відновлюємо збережені налаштування чату
    loadChatSettings()

    // Відновлюємо збережені налаштування плеєра (автоперемикання серії)
    loadPlayerSettings()

    // Значки коментарів на кнопках серій
    if (!props.archive) loadEpisodeCommentCounts()

    // Реєструємо обробники для перетягування розділювача режиму порівняння
    document.addEventListener('mousemove', onDocumentMousemove)
    document.addEventListener('mouseup', onDocumentMouseup)
    document.addEventListener('touchmove', onDocumentTouchmove, {
      passive: true
    })
    document.addEventListener('touchend', onDocumentTouchend)

    nextTick(() => {
      updateEpisodesScrollState()
      if (episodesContainerRef.value && window.ResizeObserver) {
        episodesResizeObserver = new ResizeObserver(() =>
          updateEpisodesScrollState()
        )
        episodesResizeObserver.observe(episodesContainerRef.value)
      }
    })

    // Listeners for fullscreen
    const fsEvents = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'MSFullscreenChange'
    ]
    fsEvents.forEach((evt) =>
      document.addEventListener(evt, handleFullscreenChange)
    )

    initPlayer()
  }
})

onBeforeUnmount(() => {
  // Шейдер відчіпляємо ДО знищення плеєра — він тримає посилання на <video>
  destroyShader()
  destroyHls()
  watchCurrentEpisode.value = null
  if (debugInterval) {
    clearInterval(debugInterval)
    debugInterval = null
  }

  if (import.meta.client) {
    document.body.style.cursor = 'default'
    document.body.style.overflow = ''

    window.removeEventListener('keydown', keyHandler)
    window.removeEventListener('resize', updateEpisodesScrollState)

    document.removeEventListener('mousemove', onDocumentMousemove)
    document.removeEventListener('mouseup', onDocumentMouseup)
    document.removeEventListener('touchmove', onDocumentTouchmove)
    document.removeEventListener('touchend', onDocumentTouchend)
    if (episodesResizeObserver) {
      episodesResizeObserver.disconnect()
      episodesResizeObserver = null
    }
    const fsEvents = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'MSFullscreenChange'
    ]
    fsEvents.forEach((evt) =>
      document.removeEventListener(evt, handleFullscreenChange)
    )

    // Очищаем таймаут скролла
    if (scrollTimeout) {
      clearTimeout(scrollTimeout)
      scrollTimeout = null
    }

    // Очищаем інтервал оновлення кешу
    if (updateInterval) {
      clearInterval(updateInterval)
      updateInterval = null
    }
  }
})

// --- Якість ---
// Рівні драбини з мастер-плейлиста (у записів ефірів їх кілька: 1080p60,
// 720p, 480p, тільки звук). -1 — авто. Нативний HLS Safari рівнів назовні
// не віддає — там якість обирає сам браузер, і меню не показуємо
const qualityLevels = ref([])
const qualityLevel = ref(-1)

const qualityLabel = (lvl) => {
  if (!lvl.height) return 'Тільки звук'
  const fps = Math.round(Number(lvl.frameRate) || 0)
  return `${lvl.height}p${fps > 30 ? fps : ''}`
}

const setQualityLevel = (value) => {
  qualityLevel.value = Number(value)
  if (hlsInstance.value) hlsInstance.value.currentLevel = qualityLevel.value
}

const destroyHls = () => {
  qualityLevels.value = []
  qualityLevel.value = -1
  if (hlsInstance.value) {
    hlsInstance.value.stopLoad()
    hlsInstance.value.detachMedia()
    hlsInstance.value.destroy()
    hlsInstance.value = null
  }
}

// --- Anime4K шейдер ---

// Той самий тест, що й Anime4KJS.VideoUpscaler.isSupported(), але без
// довантаження всієї бібліотеки — щоб просто вирішити, чи показувати меню
const checkShaderSupport = () => {
  if (!import.meta.client) return false
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl')
    if (!gl) return false
    if (!gl.getExtension('OES_texture_float')) return false
    if (!gl.getExtension('OES_texture_float_linear')) return false
    return true
  } catch (e) {
    return false
  }
}

const ensureAnime4K = async () => {
  if (Anime4KJS) return true
  if (!import.meta.client) return false
  try {
    Anime4KJS = await import('anime4k.js')
    return true
  } catch (e) {
    console.error('[Player] Failed to load anime4k.js', e)
    shaderSupported.value = false
    return false
  }
}

const getShaderConfig = (mode, quality, scale) => {
  if (!Anime4KJS || mode === 'off') return null

  let config
  if (mode === 'restore') {
    // Тільки CNN-реставрація/денойз на нативній роздільності — без апскейлу x2
    const map = {
      s: Anime4KJS.Anime4K_Restore_CNN_S,
      m: Anime4KJS.Anime4K_Restore_CNN_M,
      l: Anime4KJS.Anime4K_Restore_CNN_L,
      vl: Anime4KJS.Anime4K_Restore_CNN_VL,
      ul: Anime4KJS.Anime4K_Restore_CNN_UL
    }
    const restoreCtor = map[quality] || Anime4KJS.Anime4K_Restore_CNN_M
    config = restoreCtor
      ? [Anime4KJS.Anime4K_Clamp_Highlights, restoreCtor]
      : null
  } else if (mode === 'simple') {
    const map = {
      s: Anime4KJS.ANIME4KJS_SIMPLE_S_2X,
      m: Anime4KJS.ANIME4KJS_SIMPLE_M_2X,
      l: Anime4KJS.ANIME4KJS_SIMPLE_L_2X,
      vl: Anime4KJS.ANIME4KJS_SIMPLE_VL_2X,
      ul: Anime4KJS.ANIME4KJS_SIMPLE_UL_2X
    }
    config = map[quality] || Anime4KJS.ANIME4KJS_SIMPLE_M_2X
  } else {
    const [tier, letter] = mode.split('-') // 'lower'|'higher', 'a'|'b'|'c'
    const prefix =
      tier === 'lower' ? 'ANIME4K_LOWEREND_MODE_' : 'ANIME4K_HIGHEREND_MODE_'
    const suffix = letter.toUpperCase() + (quality === 'fast' ? '_FAST' : '')
    const preset = Anime4KJS[prefix + suffix]
    config = preset ? Anime4KJS.ignoreAutoDownscalePre(preset) : null
  }

  if (!config) return null
  // Лише "4k" реально потребує CNN-апскейлу; "native" і "2k" працюють на
  // нативній роздільності (для "2k" збільшення відбувається вже при показі,
  // без зайвого CNN-проходу)
  return capUpscaleStages(config, scale === '4k' ? 1 : 0)
}

// --- Композитор: перемальовує прихований канвас бібліотеки на видимий,
// підганяючи розмір під обрану "Роздільність" (2K = розтягнутий показ
// нативного кадру, без додаткового CNN-проходу)
let compositorRaf = null

const stopCompositor = () => {
  if (compositorRaf != null) {
    cancelAnimationFrame(compositorRaf)
    compositorRaf = null
  }
}

const getDisplayTargetSize = () => {
  const video = videoRef.value
  const src = shaderCanvasRef.value
  if (!video || !src || !src.width || !src.height) return null

  if (shaderScale.value === '2k' && shaderMode.value !== 'restore') {
    const targetH = 1440
    const targetW = Math.round((video.videoWidth * targetH) / video.videoHeight)
    return { w: targetW, h: targetH }
  }
  return { w: src.width, h: src.height }
}

const runCompositor = () => {
  compositorRaf = requestAnimationFrame(runCompositor)

  const src = shaderCanvasRef.value
  const dst = shaderDisplayCanvasRef.value
  if (!src || !dst) return

  const size = getDisplayTargetSize()
  if (!size) return

  if (dst.width !== size.w || dst.height !== size.h) {
    dst.width = size.w
    dst.height = size.h
  }

  const ctx = dst.getContext('2d')
  ctx.drawImage(src, 0, 0, size.w, size.h)
}

const startCompositor = () => {
  if (compositorRaf != null) return
  runCompositor()
}

const destroyShader = () => {
  stopCompositor()
  if (videoUpscaler.value) {
    videoUpscaler.value.detachVideo()
    videoUpscaler.value = null
  }
}

const setupShader = () => {
  destroyShader()

  if (
    !Anime4KJS ||
    !shaderSupported.value ||
    shaderMode.value === 'off' ||
    !videoRef.value ||
    !shaderCanvasRef.value
  ) {
    return
  }

  const shaderConfig = getShaderConfig(
    shaderMode.value,
    shaderQuality.value,
    shaderScale.value
  )
  if (!shaderConfig) return

  const upscaler = new Anime4KJS.VideoUpscaler(shaderConfig)
  upscaler.attachVideo(videoRef.value, shaderCanvasRef.value)
  upscaler.start()
  videoUpscaler.value = upscaler
  startCompositor()
}

watch(shaderMode, async (mode, oldMode) => {
  if (mode === oldMode) return
  shaderQuality.value = defaultShaderQuality(mode)

  if (mode === 'off') {
    // Якщо вимикаємо шейдер — вимикаємо і режим порівняння
    compareMode.value = false
    destroyShader()
    return
  }

  const ok = await ensureAnime4K()
  if (!ok) {
    shaderMode.value = 'off'
    return
  }
  setupShader()
})

watch(shaderQuality, () => {
  if (shaderMode.value !== 'off') setupShader()
})

watch(shaderScale, () => {
  if (shaderMode.value !== 'off') setupShader()
})

// Автозбереження налаштувань апскейлу в localStorage
watch([shaderMode, shaderQuality, shaderScale, compareMode], saveShaderSettings)

// Автозбереження налаштувань плеєра в localStorage
watch(autoNextEpisode, savePlayerSettings)

// Автозбереження налаштувань чату в localStorage
watch(
  [
    chatShow,
    chatSize,
    chatHeight,
    chatOpacity,
    chatMode,
    chatSource,
    chatSourceChosen
  ],
  saveChatSettings
)

// --- Режим порівняння: перетягування розділювача ---
const onDividerMousedown = (e) => {
  e.preventDefault()
  isDraggingDivider.value = true
}

const onDividerTouchstart = () => {
  isDraggingDivider.value = true
}

const updateDividerFromEvent = (clientX) => {
  const container = compareContainerRef.value
  if (!container) return
  const rect = container.getBoundingClientRect()
  const pct = Math.min(
    100,
    Math.max(0, ((clientX - rect.left) / rect.width) * 100)
  )
  compareDividerPct.value = pct
}

const onDocumentMousemove = (e) => {
  if (!isDraggingDivider.value) return
  updateDividerFromEvent(e.clientX)
}

const onDocumentMouseup = () => {
  isDraggingDivider.value = false
}

const onDocumentTouchmove = (e) => {
  if (!isDraggingDivider.value) return
  updateDividerFromEvent(e.touches[0].clientX)
}

const onDocumentTouchend = () => {
  isDraggingDivider.value = false
}

// --- Дебаг-інформація ---
const collectDebugStats = () => {
  if (!import.meta.client || !videoRef.value) return null
  const video = videoRef.value
  const quality =
    typeof video.getVideoPlaybackQuality === 'function'
      ? video.getVideoPlaybackQuality()
      : null

  let bufferedAhead = 0
  try {
    for (let i = 0; i < video.buffered.length; i++) {
      if (
        video.currentTime >= video.buffered.start(i) &&
        video.currentTime <= video.buffered.end(i)
      ) {
        bufferedAhead = video.buffered.end(i) - video.currentTime
        break
      }
    }
  } catch (e) {}

  let hls = null
  if (hlsInstance.value) {
    const level = hlsInstance.value.levels?.[hlsInstance.value.currentLevel]
    hls = {
      engine: 'hls.js',
      level: hlsInstance.value.currentLevel,
      bitrate: level?.bitrate ? `${Math.round(level.bitrate / 1000)} kbps` : '—',
      bandwidthEstimate: hlsInstance.value.bandwidthEstimate
        ? `${Math.round(hlsInstance.value.bandwidthEstimate / 1000)} kbps`
        : '—'
    }
  } else if (video.currentSrc) {
    hls = { engine: 'native HLS' }
  }

  const modeLabel =
    shaderModeOptions.find((o) => o.value === shaderMode.value)?.label ||
    shaderMode.value

  return {
    videoRes: `${video.videoWidth}x${video.videoHeight}`,
    shaderCanvasRes: shaderCanvasRef.value
      ? `${shaderCanvasRef.value.width}x${shaderCanvasRef.value.height}`
      : '—',
    displayCanvasRes: shaderDisplayCanvasRef.value
      ? `${shaderDisplayCanvasRef.value.width}x${shaderDisplayCanvasRef.value.height}`
      : '—',
    shaderMode: modeLabel,
    shaderQuality: shaderQuality.value || '—',
    shaderScale:
      shaderMode.value === 'restore'
        ? 'Нативна (без апскейлу)'
        : shaderScaleOptions.find((o) => o.value === shaderScale.value)
            ?.label || shaderScale.value,
    shaderActive: shaderActive.value,
    shaderSupported: shaderSupported.value,
    currentTime: formatDuration(video.currentTime),
    duration: formatDuration(video.duration),
    bufferedAhead: `${bufferedAhead.toFixed(1)}с`,
    droppedFrames: quality
      ? `${quality.droppedVideoFrames}/${quality.totalVideoFrames}`
      : '—',
    playbackRate: `${video.playbackRate}x`,
    readyState: video.readyState,
    networkState: video.networkState,
    hls
  }
}

watch(showDebugInfo, (val) => {
  if (debugInterval) {
    clearInterval(debugInterval)
    debugInterval = null
  }
  if (val) {
    debugStats.value = collectDebugStats()
    debugInterval = setInterval(() => {
      debugStats.value = collectDebugStats()
    }, 500)
  } else {
    debugStats.value = null
  }
})

const initPlayer = () => {
  if (!props.anime?.episodes?.length) return

  if (import.meta.client && props.archive) {
    // Запис ефіру: ?t= з «поділитись з цього моменту» перебиває збережене
    const at = parseFloat(route.query.t)
    if (Number.isFinite(at) && at > 0) {
      loadEpisode(0, false, at)
      return
    }
  }

  if (import.meta.client) {
    // Перевіряємо query param ?ep= (з сторінки завантажень)
    const epParam = route.query.ep
    if (epParam !== undefined) {
      const epIndex = parseInt(epParam)
      if (!isNaN(epIndex) && props.anime.episodes[epIndex]) {
        current.value = epIndex
        // ?t= — секунда серії: з неї веде «дивитись з цього моменту» у кліпах
        const at = parseFloat(route.query.t)
        loadEpisode(epIndex, false, Number.isFinite(at) && at > 0 ? at : 0)
        return
      }
    }

    // Відновлення збереженого прогресу
    try {
      const routeName = progressKey.value
      if (routeName) {
        const saved = JSON.parse(localStorage.getItem(routeName))
        if (saved && saved.ep !== undefined) {
          if (props.anime.episodes[saved.ep]) {
            current.value = saved.ep
            nextTick(() => {
              volume.value = saved.volume || 1
            })
            loadEpisode(saved.ep, false, saved.time)
            return
          }
        }
      }
    } catch (e) {
      console.error('Error loading saved state:', e)
    }
  }

  // Тут ще нічого не дивились — але, можливо, дивились на іншому
  // пристрої: для залогіненого питаємо серверну історію
  if (import.meta.client && accountToken.value && !props.archive) {
    resumeFromServer().then((ok) => {
      if (!ok) loadEpisode(0, false)
    })
    return
  }

  loadEpisode(0, false)
}

const resumeFromServer = async () => {
  try {
    const { item } = await $fetch(
      `${config.public.apiBase}/api/account/history/anime/${encodeURIComponent(route.params.name)}`,
      { headers: { Authorization: `Bearer ${accountToken.value}` } }
    )
    if (!item || !props.anime?.episodes?.[item.episodeIndex]) return false
    current.value = item.episodeIndex
    loadEpisode(item.episodeIndex, false, Math.floor(item.position) || 0)
    return true
  } catch (e) {
    return false
  }
}

const loadEpisode = (epIndex, autoplay = false, startTime = 0) => {
  destroyHls()
  failed.value = false
  current.value = epIndex

  const episode = props.anime.episodes[epIndex]
  if (!episode) return

  // Встановлюємо контекст епізоду для SW кешування
  if (import.meta.client && hlsCacheSupported.value && !props.archive) {
    const animeSlug = route.params.name
    const token =
      props?.anime?.privat && import.meta.client && window.videoToken
        ? window.videoToken
        : null
    setEpisodeContext(animeSlug, String(epIndex + 1), animeSlug, token)
    // Перевіряємо чи вже завантажено
    checkEpisodeDownloaded(animeSlug, String(epIndex + 1))
  }

  if (import.meta.client) {
    nextTick(() => {
      const container = document?.querySelector('.episodes-container')
      const episodeBtnElement = container?.querySelector('.activeEpisode')

      if (episodeBtnElement && container) {
        // Получаем координаты обоих элементов относительно окна браузера
        const elementRect = episodeBtnElement.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()

        // Вычисляем расстояние от верха контейнера до элемента
        // Текущий скролл контейнера + (координата элемента - координата контейнера)
        const relativeTop =
          elementRect.top - containerRect.top + container.scrollTop

        // Вычисляем позицию центра
        const targetScrollTop =
          relativeTop -
          container.offsetHeight / 2 +
          episodeBtnElement.offsetHeight / 2

        container.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth'
        })
      }
    })
  }

  // Загрузка чата
  if (episode.chat && episode.chat !== '') {
    chatAvailable.value = true // show button immediately, hide if load fails
    if (!chatSourceChosen.value) chatSource.value = 'archive'
    getChat(episode.chat)
  } else {
    // Панель гасимо лише коли в ній показувався саме архів — Twitch і чат
    // сайту від наявності архіву не залежать
    dropArchiveSource()
    chatAvailable.value = false
    chatMessages.value = []
  }

  // Если включен режим Heroku (iframe/прямая ссылка)
  if (heroku.value) {
    if (videoRef.value) {
      videoRef.value.src = episode.heroku
      videoRef.value.currentTime = startTime
      if (autoplay) videoRef.value.play()
    }
    return
  }

  // HLS Logic
  let source = episode.hls
  if (props?.anime?.privat && import.meta.client) {
    source = source + `?t=${window.videoToken}`
  }
  checkEpisode(source) // Проверка доступности

  // Нативний HLS лишаємо тільки справжньому WebKit (iOS, Safari) та тим
  // пристроям, які MSE взагалі не мають (Smart TV на WebOS/Tizen — там
  // Hls.isSupported() поверне false). Усі інші — Android, Chrome, Firefox,
  // Edge — грають через hls.js: тільки так ми керуємо буфером і рівнями.
  const nativeHlsSupported = videoRef.value?.canPlayType(
    'application/vnd.apple.mpegurl'
  )

  if ((!nativeHlsSupported || !isWebKitBrowser()) && Hls.isSupported()) {
    const hls = new Hls(hlsConfig.value)
    hlsInstance.value = hls

    hls.loadSource(source)
    hls.attachMedia(videoRef.value)

    hls.on(Hls.Events.MANIFEST_PARSED, (_e, data) => {
      qualityLevels.value = (data?.levels || [])
        .map((lvl, index) => ({ index, height: lvl.height || 0, label: qualityLabel(lvl) }))
        .sort((a, b) => b.height - a.height)
      if (startTime > 0 && videoRef.value) {
        videoRef.value.currentTime = startTime
      }
      if (autoplay && videoRef.value) {
        videoRef.value.play().catch(() => {})
      }
    })

    hls.on(Hls.Events.ERROR, (event, data) => {
      // Не фатально, але саме через це буфер "застрягає" на кількох секундах:
      // пам'ять SourceBuffer вичерпано і hls.js зменшує цільову довжину буфера
      if (data.details === Hls.ErrorDetails.BUFFER_FULL_ERROR) {
        console.warn(
          '[Player] SourceBuffer full, hls.js зменшує буфер. maxMaxBufferLength =',
          hls.config.maxMaxBufferLength
        )
      }
      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            hls.startLoad()
            break
          case Hls.ErrorTypes.MEDIA_ERROR:
            hls.recoverMediaError()
            break
          default:
            destroyHls()
            failed.value = true
            break
        }
      }
    })
  } else if (videoRef.value) {
    // Native HLS — Safari, iOS та Smart TV (WebOS, Tizen) і інші браузери,
    // що підтримують HLS нативно або частково (canPlayType може повертати '' на TV)
    videoRef.value.src = source
    videoRef.value.addEventListener(
      'loadedmetadata',
      () => {
        if (startTime > 0) videoRef.value.currentTime = startTime
        if (autoplay) videoRef.value.play()
      },
      { once: true }
    )
    videoRef.value.addEventListener(
      'error',
      () => {
        // Якщо нативний HLS теж не спрацював — показуємо помилку
        failed.value = true
      },
      { once: true }
    )
  }

  // Восстановление скорости
  controls.rate.value = savedVideoSpeed.value

  // Аналитика
  // gtag('event', `anime_watch_${route.params.name}_${epIndex}`, {
  //   episode: epIndex
  // })

  if (process.client && window.zaraz && !props.archive) {
    window?.zaraz?.track(`anime_watch_${route.params.name}_${epIndex}`, {
      anime: route.params.name,
      episode: epIndex
    })
  }
}

const changeEpisode = (id, autoplay, videoTime = 0) => {
  loadEpisode(id, autoplay, videoTime)
}

const checkEpisode = (url) => {
  fetch(url, { method: 'HEAD' })
    .then((res) => {
      if (!res.ok) failed.value = true
    })
    .catch(() => {
      failed.value = true
    })
}

// Перевірка чи епізод вже завантажено (повністю)
const episodeCacheInfo = ref(null) // { saved, total, isComplete }
const allEpisodesCache = ref({}) // { [ind]: { saved, total, isComplete } }

const loadAllEpisodesCache = async () => {
  try {
    const stats = await getEpisodeStats()
    if (!stats) return
    const animeSlug = route.params.name
    const map = {}
    for (let i = 0; i < props.anime.episodes.length; i++) {
      const key = `${animeSlug}|||${String(i + 1)}`
      const ep = stats[key]
      if (ep) {
        const saved = ep.segments.length
        const total = ep.totalSegments || saved
        if (total > 0) {
          const percent = (saved / total) * 100
          // Вважаємо завантаженою якщо є totalSegments і >95%
          const isComplete = ep.totalSegments && percent >= 95
          map[i] = {
            saved,
            total,
            isComplete
          }
        }
      }
    }
    allEpisodesCache.value = map
  } catch (e) {
    console.error('[loadAllEpisodesCache] error:', e)
  }
}

const checkEpisodeDownloaded = async (animeSlug, episodeId) => {
  // Просто оновлюємо episodeCacheInfo з allEpisodesCache
  const epIndex = parseInt(episodeId) - 1
  const cached = allEpisodesCache.value[epIndex]
  episodeCacheInfo.value = cached || null
}

// Реактивний computed для isDownloaded
const isDownloadedComputed = computed(() => {
  const cached = allEpisodesCache.value[current.value]
  return cached?.isComplete || false
})

// Використовуємо watchEffect щоб синхронізувати ref з computed
watchEffect(() => {
  isDownloaded.value = isDownloadedComputed.value
})

// Періодичне оновлення статусу серій (кожні 5 сек якщо є активні завантаження)
let updateInterval = null
watch(
  downloadProgress,
  (val) => {
    const hasActiveDownloads = Object.values(val).some(
      (d) => d.status === 'downloading' || d.status === 'fetching_playlist'
    )

    // Оновлюємо іконки для епізодів що завантажуються в реальному часі
    Object.entries(val).forEach(([key, prog]) => {
      if (prog.status === 'downloading' && prog.total > 0) {
        // Витягуємо індекс епізоду з ключа (animeSlug-episodeId)
        const episodeId = key.split('-').pop()
        const epIndex = parseInt(episodeId) - 1
        if (epIndex >= 0 && epIndex < props.anime.episodes.length) {
          const percent = (prog.done / prog.total) * 100
          allEpisodesCache.value = {
            ...allEpisodesCache.value,
            [epIndex]: {
              saved: prog.done,
              total: prog.total,
              isComplete: percent >= 95
            }
          }
        }
      }
    })

    if (hasActiveDownloads && !updateInterval) {
      updateInterval = setInterval(() => {
        loadAllEpisodesCache()
      }, 5000)
    } else if (!hasActiveDownloads && updateInterval) {
      clearInterval(updateInterval)
      updateInterval = null
      // Фінальне оновлення після завершення
      loadAllEpisodesCache()
    }
  },
  { deep: true }
)
// Завантаження поточного епізоду
const handleDownloadEpisode = () => {
  if (isDownloading.value) return
  const episode = props.anime.episodes[current.value]
  if (!episode?.hls) return

  const animeSlug = route.params.name
  const episodeId = String(current.value + 1)

  // Для приватних аніме додаємо токен до URL
  let hlsUrl = episode.hls
  if (props?.anime?.privat && import.meta.client && window.videoToken) {
    hlsUrl = `${episode.hls}?t=${window.videoToken}`
  }

  downloadEpisodeSW(animeSlug, episodeId, animeSlug, hlsUrl, {
    animeTitle: props.anime?.title || animeSlug,
    episodeTitle: episode?.title || `Серія ${episodeId}`,
    posterUrl: props.anime?.poster?.url
      ? `${config.public.apiBase}${props.anime.poster.url}?format=webp&width=80&quality=70`
      : null
  })
}

// --- Кліпи ---
// Редактор кліпу відкривається панеллю під плеєром (components/clip/Editor):
// прев'ю — цей самий <video>, сегменти — з цього самого hls.js. З закритих
// записів кліпи робить лише Монобаза, і бачать їх теж лише вони (сервер
// позначає такий кліп restricted)
const clipOpen = ref(false)
const canClip = computed(
  () =>
    (!props.anime?.privat || !!user.value?.privat) &&
    !!props.anime?.episodes?.[current.value]?.hls
)

// Сегменти закритого запису вимагають токен у кожному запиті — hls.js
// дописує його сам (xhrSetup), а нарізка ходить за сегментами напряму
const signClipUrl = (url) => {
  if (!props.anime?.privat || !import.meta.client || !window.videoToken) return url
  const u = new URL(url, window.location.href)
  u.searchParams.set('t', window.videoToken)
  return u.toString()
}

const clipSource = computed(() => {
  const episode = props.anime?.episodes?.[current.value]
  const episodeTitle = episode?.title || `Серія ${current.value + 1}`
  const animeTitle = props.anime?.title || route.params.name
  if (props.archive) {
    return {
      sourceType: 'archive',
      archiveId: props.archive.id,
      label: animeTitle,
      title: ''
    }
  }
  return {
    sourceType: 'anime',
    animeSlug: route.params.name,
    episodeIndex: current.value,
    label: `${animeTitle} · ${episodeTitle}`,
    title: ''
  }
})

const getClipVideo = () => videoRef.value

const getClipFragments = async () => {
  // Safari грає HLS сам — тоді розбираємо плейлист серії власноруч
  const fragments =
    fragmentsFromHls(hlsInstance.value) ||
    (await fragmentsFromPlaylist(props.anime.episodes[current.value].hls, signClipUrl))
  return fragments.map((frag) => ({
    ...frag,
    url: signClipUrl(frag.url),
    init: frag.init ? signClipUrl(frag.init) : null
  }))
}

const openClipEditor = async () => {
  if (!canClip.value) return
  // У фулскріні панелі під плеєром не видно
  if (isFullscreen.value) await toggleFullScreen()
  clipOpen.value = true
  nextTick(() => {
    document.getElementById('clip-editor')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  })
}

// Інша серія — старий відрізок уже ні про що
watch(current, () => {
  clipOpen.value = false
})

// --- Плейлист для зовнішніх плеєрів (VLC) ---
const { downloadPlaylist, copyPlaylistUrl } = useHlsPlaylist()
const playlistCopied = ref(false)
const playlistSaving = ref(false)

// URL плейлиста поточного епізоду (з токеном для приватних аніме)
const currentPlaylistUrl = computed(() => {
  const hls = props.anime?.episodes?.[current.value]?.hls
  if (!hls) return null
  if (props?.anime?.privat && import.meta.client && window.videoToken) {
    return `${hls}?t=${window.videoToken}`
  }
  return hls
})

const currentPlaylistName = computed(() => {
  const episode = props.anime?.episodes?.[current.value]
  const animeTitle = props.anime?.title || route.params.name
  return `${animeTitle} - ${episode?.title || `Серія ${current.value + 1}`}`
})

const handleCopyPlaylistUrl = async () => {
  if (!currentPlaylistUrl.value) return
  const ok = await copyPlaylistUrl(currentPlaylistUrl.value)
  if (!ok) return
  playlistCopied.value = true
  setTimeout(() => (playlistCopied.value = false), 2000)
}

const handleDownloadPlaylist = async () => {
  if (!currentPlaylistUrl.value || playlistSaving.value) return
  playlistSaving.value = true
  try {
    await downloadPlaylist(currentPlaylistUrl.value, currentPlaylistName.value)
  } catch (e) {
    console.error('[Player] playlist download error:', e)
  } finally {
    playlistSaving.value = false
  }
}

// Прогрес завантаження поточного епізоду
const currentDownloadProgress = computed(() => {
  const animeSlug = route.params.name
  const episodeId = String(current.value + 1)
  const key = `${animeSlug}-${episodeId}`
  return downloadProgress.value[key] || null
})

// Стан завантаження на основі прогресу
watchEffect(() => {
  const p = currentDownloadProgress.value
  isDownloading.value =
    p?.status === 'downloading' || p?.status === 'fetching_playlist'
  if (p?.status === 'done') {
    isDownloaded.value = true
    episodeCacheInfo.value = null
    // Оновлюємо кеш для поточного епізоду
    const idx = current.value
    const total = p.total || 0
    if (total > 0) {
      allEpisodesCache.value = {
        ...allEpisodesCache.value,
        [idx]: { saved: total, total, isComplete: true }
      }
    }
  }
})

// --- Кількість коментарів на кнопках серій ---
// Гілка серії — той самий page-id, що й у блока під плеєром
const episodeCommentCounts = ref({})

const loadEpisodeCommentCounts = async () => {
  if (!import.meta.client) return
  const slug = route.params.name
  const total = props.anime?.episodes?.length || 0
  if (!slug || !total) return

  const ids = Array.from({ length: total }, (_, i) => `${slug}-ep${i + 1}`)

  try {
    const { counts } = await $fetch('/api/chatCounts', {
      query: { ids: ids.join(',') }
    })
    episodeCommentCounts.value = counts || {}
  } catch (e) {
    episodeCommentCounts.value = {}
  }
}

const episodeCommentCount = (index) =>
  episodeCommentCounts.value[`${route.params.name}-ep${index + 1}`] || 0

// --- Бічна панель чату (режим кінотеатру) ---
// Одна панель праворуч, три можливих джерела. Архів прив'язаний до таймкоду
// серії, тому доступний лише там, де він узагалі є.
const chatSourceOptions = computed(() => [
  { id: 'site', label: 'Чат сайту', available: true },
  { id: 'archive', label: 'Архів стріму', available: chatAvailable.value }
])

// Чат сайту — це iframe: поверх відео його не намалюєш, тому в режимі попапа
// він іде в те саме плаваюче вікно, що й поза плеєром (воно вміє фулскрін)
const isFrameChat = computed(() => chatSource.value === 'site')
const isPopupMode = computed(() => chatMode.value === 'popup')

// Попап архіву — легкий оверлей усередині плеєра, з власним розміром
const chatPipActive = computed(() => isPopupMode.value && !isFrameChat.value)

const chatPanelOpen = computed(
  () =>
    chatShow.value &&
    !isPopupMode.value &&
    (isFrameChat.value || chatAvailable.value)
)

// Масштаб фрейму в панелі — свій, як і у вікна
const panelZoom = ref(1)

// У плеєрі логічно починати з чату серії: людина дивиться конкретну серію.
// Явний вибір користувача не чіпаємо — тільки підставляємо початковий.
watch(chatPanelOpen, (val) => {
  if (!val || !isFrameChat.value || chatScopeChosen.value) return
  if (chatScopes.value.some((s) => s.id === 'episode')) {
    selectChatScope('episode')
  }
})

// Поки панель відкрита, чат «видно» — віджет за цим ховає бабли й кнопку
const chatPanelVisible = useState('siteChatPanelOpen', () => false)
watchEffect(() => (chatPanelVisible.value = chatPanelOpen.value))
onBeforeUnmount(() => (chatPanelVisible.value = false))

// На телефоні поза фулскріном панель управління обрізати нема сенсу: місця й
// так мало, кнопки перетворюються на кашу. Там вона лишається на всю ширину,
// а чат ховається під нею
const limitControlsWidth = computed(
  () => chatPanelOpen.value && (!isMobile.value || isFullscreen.value)
)

// Попросили чат ззовні (кнопка у фулскріні) — відкриваємо саме кінотеатр
watch(playerChatRequest, () => {
  chatSource.value = 'site'
  chatMode.value = 'theater'
  chatShow.value = true
})

// Плаваюче вікно чату сайту — спільний стан з SiteChatWidget
const sitePopupOpen = computed(
  () => chatShow.value && isPopupMode.value && isFrameChat.value
)

// Керуємо вікном лише поки самі його попросили: закривати чужий чат,
// відкритий кнопкою на сторінці, ми не маємо права
let wasOpenedByPlayer = false

watch(sitePopupOpen, (val) => {
  if (val) twitchChatOpen.value = true
  else if (twitchChatOpen.value && wasOpenedByPlayer) {
    twitchChatOpen.value = false
  }
  wasOpenedByPlayer = val
})

// У режимі вікна тумблер у меню і саме вікно — це один стан, тому тягнемо його
// і в зворотний бік: чат могли відкрити хрестиком, круглою кнопкою чи баблом,
// і меню не повинно брехати, що він вимкнений
watch(twitchChatOpen, (val) => {
  if (!isPopupMode.value || !isFrameChat.value) return
  chatShow.value = val
  wasOpenedByPlayer = val
})

// --- Chat Logic ---
const chatMessages = ref([])

// Оптимизация рендеринга чата с IntersectionObserver
const visibleChatMessages = ref([])
const chatObserver = ref(null)

// Вспомогательная функция для времени
const parseTimeStr = (str) => {
  const parts = str.split(':').map(Number)
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]
  if (parts.length === 2) return parts[0] * 60 + parts[1]
  return 0
}

const getChat = async (chatUrl) => {
  chatMessages.value = []
  if (!chatUrl) return

  try {
    // Параллельная загрузка
    // Емоції підставляє EmoteText під час рендера — тут лишається чистий текст
    const [chatText] = await Promise.all([
      fetch(chatUrl).then(async (r) => {
        if (!r.ok) {
          try {
            // Пробуем прочитать JSON ошибки
            const errorData = await r.json()
            if (errorData && errorData.error) {
              console.error('Чат не найден:', errorData.error.message)
              return ''
            }
          } catch (e) {
            // Если это не JSON, просто игнорируем
          }
          return '' // Возвращаем пустую строку при любом сбое
        }
        return r.text() // Если всё хорошо, возвращаем текст чата
      })
    ])

    if (chatText === '') {
      dropArchiveSource()
      chatAvailable.value = false
      chatMessages.value = []
      return
    }

    const lines = chatText.split('\r\n')
    const parsed = []
    let firstTime = null

    for (const line of lines) {
      if (!line || !line.includes('] ')) continue

      // Формат: [01:02:03] user: message
      const parts = line.split('] ')
      if (parts.length < 2) continue

      const timeStr = parts[0].substring(1) // убираем [
      const absSeconds = parseTimeStr(timeStr)

      // Если это первое сообщение, считаем его оффсетом
      if (firstTime === null) firstTime = absSeconds

      // Автор и сообщение
      const content = parts.slice(1).join('] ')
      const authorMatch = content.match(/^([^:]+):\s*(.*)$/)

      if (authorMatch) {
        const author = authorMatch[1]
        let text = authorMatch[2]

        parsed.push({
          time: formatDuration(absSeconds - firstTime),
          timesec: absSeconds - firstTime,
          author,
          color: getRandomColor(),
          text
        })
      }
    }
    chatMessages.value = parsed
    chatAvailable.value = parsed.length > 0
  } catch (e) {
    console.error('Chat loading error', e)
    dropArchiveSource()
    chatAvailable.value = false
    chatMessages.value = []
  }
}

const visibleChat = computed(() => {
  if (!chatShow.value || chatMessages.value.length === 0) return []

  const now = currentTime.value
  const passed = chatMessages.value.filter((m) => m.timesec <= now)

  // Ограничиваем количество отображаемых сообщений для производительности
  const recentMessages = passed.slice(-100)

  // Используем виртуализацию для больших списков
  if (recentMessages.length > 50) {
    return recentMessages.slice(-50)
  }

  return recentMessages
})

// Оптимизированный авто-скролл с debounce
let scrollTimeout = null
watch(visibleChat, () => {
  if (!chatShow.value) return

  if (scrollTimeout) {
    clearTimeout(scrollTimeout)
  }

  scrollTimeout = setTimeout(() => {
    if (chatContainerRef.value) {
      nextTick(() => {
        if (chatContainerRef.value) {
          chatContainerRef.value.scrollTop = chatContainerRef.value.scrollHeight
        }
      })
    }

    if (chatPipRef.value) {
      nextTick(() => {
        if (chatPipRef.value) {
          chatPipRef.value.scrollTop = chatPipRef.value.scrollHeight
        }
      })
    }
    scrollTimeout = null
  }, 50) // Debounce 50ms
})

// --- Історія переглядів у профілі ---
// Для залогіненого прогрес їде ще й на сервер: так його видно в профілі, а
// серія продовжується з того ж місця на іншому пристрої. Не частіше ніж раз
// на пів хвилини, плюс одразу на паузі, зміні серії й виході зі сторінки
const PROGRESS_SYNC_MS = 30000
let lastProgressSync = 0

const syncProgress = (force = false) => {
  if (!import.meta.client || !accountToken.value || !videoRef.value || props.archive) return
  const position = Math.floor(videoRef.value.currentTime || 0)
  if (position < 5) return
  if (!force && Date.now() - lastProgressSync < PROGRESS_SYNC_MS) return
  lastProgressSync = Date.now()

  const duration = Number.isFinite(videoRef.value.duration) ? videoRef.value.duration : null
  // keepalive — щоб запит доїхав і тоді, коли вкладку вже закривають
  fetch(`${config.public.apiBase}/api/account/history`, {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accountToken.value}`
    },
    body: JSON.stringify({
      animeSlug: route.params.name,
      episodeIndex: current.value,
      position,
      duration
    })
  }).catch(() => {})
}

watch(playing, (isPlaying) => {
  if (!isPlaying) syncProgress(true)
})

watch(current, (next, prev) => {
  if (prev === undefined || next === prev) return
  lastProgressSync = 0
})

if (import.meta.client) {
  const onHide = () => syncProgress(true)
  onMounted(() => window.addEventListener('pagehide', onHide))
  onBeforeUnmount(() => {
    syncProgress(true)
    window.removeEventListener('pagehide', onHide)
  })
}

// Сохранение прогресса
watch(currentTime, (val) => {
  syncProgress()
  if (!import.meta.client) return
  const intVal = Math.floor(val)
  if (intVal > 0 && intVal % 5 === 0) {
    localStorage.setItem(
      progressKey.value,
      JSON.stringify({
        ep: current.value,
        volume: volume.value,
        time: intVal
      })
    )
  }
})

// Скорость воспроизведения
const changeRate = () => {
  savedVideoSpeed.value = controls.rate.value
}

// --- Cast / AirPlay ---
const castSupported = ref(false)
const castActive = ref(false)
const castNoDevices = ref(false)

// Current HLS source tracked for sending to Chromecast
const currentCastSource = ref(null)

// --- Google Cast SDK ---
// Loaded via useHead; window.__castApiInitialized signals SDK is ready
const castContext = shallowRef(null)

const initGoogleCast = () => {
  if (!import.meta.client) return
  if (
    typeof window.cast === 'undefined' ||
    typeof window.chrome?.cast === 'undefined'
  )
    return

  const ctx = window.cast.framework.CastContext.getInstance()
  ctx.setOptions({
    receiverApplicationId:
      window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
    autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED
  })
  castContext.value = ctx
  castSupported.value = true

  ctx.addEventListener(
    window.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
    (e) => {
      const s = e.castState
      castActive.value =
        s === window.cast.framework.CastState.CONNECTED ||
        s === window.cast.framework.CastState.CONNECTING
    }
  )
}

// Called by the Cast SDK after it loads
if (import.meta.client) {
  window.__onGCastApiAvailable = (isAvailable) => {
    if (isAvailable) initGoogleCast()
  }
}

useHead({
  script: [
    {
      src: 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1',
      async: true,
      defer: true,
      tagPosition: 'bodyClose'
    }
  ]
})

// Send current video to Chromecast
const castToGoogle = () => {
  if (!castContext.value || !currentCastSource.value) return

  const session = castContext.value.getCurrentSession()
  if (!session) {
    // No session yet — open device picker, then load after connect
    castContext.value
      .requestSession()
      .then(() => {
        castToGoogle()
      })
      .catch((e) => {
        if (e !== window.chrome.cast.ErrorCode.CANCEL) {
          castNoDevices.value = true
          setTimeout(() => {
            castNoDevices.value = false
          }, 3000)
        }
      })
    return
  }

  const mediaInfo = new window.chrome.cast.media.MediaInfo(
    currentCastSource.value,
    'application/x-mpegurl'
  )
  mediaInfo.streamType = window.chrome.cast.media.StreamType.BUFFERED
  mediaInfo.metadata = new window.chrome.cast.media.GenericMediaMetadata()
  mediaInfo.metadata.title = props.anime?.title || ''
  mediaInfo.metadata.subtitle =
    props.anime?.episodes?.[current.value]?.title || ''
  if (props.anime?.poster?.url) {
    const img = new window.chrome.cast.Image(
      `${config.public.apiBase}${props.anime.poster.url}?format=webp&width=400&quality=80`
    )
    mediaInfo.metadata.images = [img]
  }

  const request = new window.chrome.cast.media.LoadRequest(mediaInfo)
  request.currentTime = currentTime.value

  session.loadMedia(request).catch((e) => {
    console.warn('Cast loadMedia failed', e)
  })
}

// --- AirPlay (Safari) ---
const initAirPlay = () => {
  if (!import.meta.client) return
  const video = videoRef.value
  if (!video) return

  if (typeof video.webkitShowPlaybackTargetPicker === 'function') {
    castSupported.value = true
    video.addEventListener(
      'webkitcurrentplaybacktargetiswirelesschanged',
      () => {
        castActive.value = !!video.webkitCurrentPlaybackTargetIsWireless
      },
      { once: false }
    )
  }
}

onMounted(() => {
  if (!import.meta.client) return
  initAirPlay()
  // Cast SDK may already be loaded (e.g. fast connection)
  if (window.cast) initGoogleCast()
  // Show button in Chrome immediately — SDK will load async
  if (!castSupported.value && window.chrome && !window.safari) {
    castSupported.value = true
  }
})

// Track current source for Chromecast
watch(
  current,
  () => {
    if (!import.meta.client) return
    const episode = props.anime?.episodes?.[current.value]
    if (!episode?.hls) return
    let src = episode.hls
    if (props?.anime?.privat && window.videoToken)
      src = `${src}?t=${window.videoToken}`
    currentCastSource.value = src
  },
  { immediate: true }
)

const toggleCast = () => {
  if (!import.meta.client) return

  const video = videoRef.value

  // Safari AirPlay — takes priority on WebKit
  if (video && typeof video.webkitShowPlaybackTargetPicker === 'function') {
    video.webkitShowPlaybackTargetPicker()
    return
  }

  // Google Cast SDK
  if (castContext.value) {
    castToGoogle()
    return
  }

  // Fallback: Remote Playback API (Chrome without Cast SDK loaded yet)
  if (video?.remote) {
    video.remote.prompt().catch((e) => {
      if (e?.name === 'NotAllowedError') {
        castNoDevices.value = true
        setTimeout(() => {
          castNoDevices.value = false
        }, 3000)
      } else {
        console.warn('Remote Playback prompt failed', e)
      }
    })
  }
}

// Re-sync cast position when switching episodes while casting
watch(current, () => {
  if (!castActive.value || !castContext.value) return
  const session = castContext.value.getCurrentSession()
  if (session) castToGoogle()
})

// --- Key Handlers ---
const keyHandler = (e) => {
  if (!import.meta.client) return
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return
  // З модифікатором клавіша належить браузеру (Cmd+L — адресний рядок)
  if (e.ctrlKey || e.metaKey || e.altKey) return

  // Листання серій — з Shift, як на YouTube: випадково перескочити на
  // іншу серію прикріше, ніж перемотати. Розбираємо e.code — фізичну
  // клавішу, щоб працювало й в українській розкладці
  if (e.shiftKey && (e.code === 'KeyN' || e.code === 'KeyP')) {
    e.preventDefault()
    if (e.code === 'KeyN') nextEpisode()
    else prevEpisode()
    showControls()
    return
  }

  if (e.code === 'KeyJ' || e.code === 'KeyL') {
    e.preventDefault()
    seekBy(e.code === 'KeyJ' ? -10 : 10)
    showControls()
    return
  }

  if (e.code === 'KeyK') {
    e.preventDefault()
    playing.value = !playing.value
    return
  }

  switch (e.keyCode) {
    case 70: // F
      toggleFullScreen()
      break
    case 77: // M
      muted.value = !muted.value
      break
    case 37: // Left
      currentTime.value -= 5
      break
    case 39: // Right
      currentTime.value += 5
      break
    case 32: // Space
      e.preventDefault()
      playing.value = !playing.value
      break
  }
}

// --- Коментарі до серії (comments.app) ---
// page-id прибитий до слуга аніме і номера серії, щоб гілка пережила і зміну
// назви серії, і переїзд відео на інший CDN
const episodeCommentsPageId = computed(
  () => `${route.params.name}-ep${current.value + 1}`
)

const episodeCommentsTitle = computed(
  () =>
    `${props.anime?.title || route.params.name} — ${
      props.anime?.episodes?.[current.value]?.title || current.value + 1
    }`
)

// ?ep= — той самий формат, який плеєр уже розуміє при відкритті сторінки
const episodeCommentsUrl = computed(() => {
  if (!import.meta.client) return ''
  return `${window.location.origin}${route.path}?ep=${current.value}`
})

const openLink = (url) => {
  if (import.meta.client && url) {
    window.open(url, '_blank')?.focus()
  }
}

// --- Скролл списку серій (стрілки для TV/пультів) ---
const updateEpisodesScrollState = () => {
  const el = episodesContainerRef.value
  if (!el) {
    canScrollEpisodesUp.value = false
    canScrollEpisodesDown.value = false
    return
  }

  canScrollEpisodesUp.value = el.scrollTop > 1
  canScrollEpisodesDown.value =
    el.scrollTop + el.clientHeight < el.scrollHeight - 1
}

const scrollEpisodes = (direction) => {
  const el = episodesContainerRef.value
  if (!el) return

  el.scrollBy({ top: direction * el.clientHeight * 0.8, behavior: 'smooth' })
}

// Курсор ховаємо разом із панеллю — але тільки у фулскріні: на сторінці
// він потрібен, щоб дотягнутись до решти сайту
watch([controlsVisible, isFullscreen], ([visible, full]) => {
  if (!import.meta.client) return
  document.body.style.cursor = full && !visible && playing.value ? 'none' : 'default'
})

// Колесо над іконкою звуку — звичний жест з ютубу
const VOLUME_STEP = 0.05

const setVolume = (value) => {
  const next = Math.min(1, Math.max(0, Number(value) || 0))
  volume.value = Math.round(next * 100) / 100
  // Витягнули з нуля — вмикаємо звук, довели до нуля — глушимо
  muted.value = next === 0
}

const onVolumeWheel = (e) => {
  const direction = e.deltaY > 0 ? -1 : 1
  const base = muted.value ? 0 : Number(volume.value) || 0
  setVolume(base + direction * VOLUME_STEP)
}

// Подвійний клік по картинці — фулскрін, як у ютубі. Два одиночні кліки,
// які браузер шле перед dblclick, встигають перемкнути паузу двічі, тож
// відтворення лишається в тому самому стані. На дотику подвійний тап —
// це перемотка біля країв кадру (див. onVideoTouchend), не фулскрін
const onVideoDblclick = () => {
  if (coarsePointer.value) return
  toggleFullScreen()
}

const fisrtPlayStart = () => {
  playing.value = !playing.value
  fisrtPlay.value = true
}

// --- Жести (як у плеєрі shiki.sgezha.art) ---
//
// Миша: клік по кадру — пауза, подвійний — фулскрін (як і було).
// Дотик (coarsePointer): тап по кадру показує/ховає панель — пауза лишається
// на кнопці, інакше кожна спроба дістатись до перемотки зупиняла серію;
// утримання пальця — 2×, поки тримаєш; подвійний тап біля лівого чи
// правого краю — ∓10 секунд, як на YouTube. Середина на подвійний тап не
// реагує: випадковий другий тап по центру не має мотати серію
const coarsePointer = ref(false)

const HOLD_AFTER = 450
const DOUBLE_TAP_WITHIN = 300
const TAP_SLOP = 12
const HOLD_RATE = 2
const EDGE_SEEK = 10
// Скільки після останнього тапу чекаємо наступний, щоб додати ще 10 с.
// Стільки ж висить і підказка з сумою
const SEEK_STREAK = 900

const holding = ref(false)
// Підказка біля краю: куди і на скільки вже перемотали серією тапів.
// { direction: 'back' | 'forward', total: секунди } або null
const seekFlash = ref(null)

let swallowNextClick = false
let holdTimer = null
let seekFlashTimer = null
let touchStartX = 0
let touchStartY = 0
let touchMoved = false
let lastTapAt = 0
let lastTapX = 0
let controlsVisibleBeforeTouch = false
// Швидкість до утримання. Саме запамʼятовуємо, а не беремо з controls.rate:
// useMediaControls підхоплює нову швидкість із події ratechange, тож після
// утримання там уже лежало б 2×
let rateBeforeHold = 1

const onPlayerTouchstart = () => {
  controlsVisibleBeforeTouch = controlsVisible.value
  showControls()
}

const onVideoClick = () => {
  if (!coarsePointer.value) {
    playing.value = !playing.value
    return
  }
  if (swallowNextClick) {
    swallowNextClick = false
    return
  }
  if (controlsVisibleBeforeTouch && playing.value) hideControls()
  else showControls()
}

const seekBy = (delta) => {
  const el = videoRef.value
  if (!el) return
  const max = Number.isFinite(el.duration) ? el.duration : Infinity
  el.currentTime = Math.max(0, Math.min(max, el.currentTime + delta))
}

// Серія тапів, як на YouTube: подвійний тап біля краю — 10 с, а кожен
// наступний швидкий тап по тому самому боці — ще +10, і підказка показує
// суму («+40 с»), щоб було видно, на скільки вже перемотали. Пауза довша за
// SEEK_STREAK або тап по іншому боці — серія починається наново
let seekStreakUntil = 0

const flashSeek = (direction) => {
  const continuing = seekFlash.value?.direction === direction && Date.now() < seekStreakUntil
  seekBy(direction === 'back' ? -EDGE_SEEK : EDGE_SEEK)
  seekFlash.value = {
    direction,
    total: (continuing ? seekFlash.value.total : 0) + EDGE_SEEK
  }
  seekStreakUntil = Date.now() + SEEK_STREAK
  if (seekFlashTimer) clearTimeout(seekFlashTimer)
  seekFlashTimer = setTimeout(() => {
    seekFlash.value = null
  }, SEEK_STREAK)
}

const cancelHold = () => {
  if (holdTimer) clearTimeout(holdTimer)
  holdTimer = null
}

const endHold = () => {
  cancelHold()
  if (!holding.value) return
  holding.value = false
  // Повертаємо ту швидкість, що була до жесту, а не одиницю: людина могла
  // дивитись на 1.5×
  if (videoRef.value) videoRef.value.playbackRate = rateBeforeHold
}

const onVideoTouchstart = (event) => {
  if (!coarsePointer.value || event.touches.length !== 1) return
  swallowNextClick = false
  const touch = event.touches[0]
  touchStartX = touch.clientX
  touchStartY = touch.clientY
  touchMoved = false

  cancelHold()
  holdTimer = setTimeout(() => {
    holdTimer = null
    const el = videoRef.value
    if (!el || el.paused || touchMoved) return
    rateBeforeHold = el.playbackRate || 1
    holding.value = true
    el.playbackRate = HOLD_RATE
  }, HOLD_AFTER)
}

const onVideoTouchmove = (event) => {
  if (!coarsePointer.value) return
  const touch = event.touches[0]
  if (!touch) return
  if (
    Math.abs(touch.clientX - touchStartX) > TAP_SLOP ||
    Math.abs(touch.clientY - touchStartY) > TAP_SLOP
  ) {
    touchMoved = true
    cancelHold()
  }
}

const onVideoTouchend = (event) => {
  if (!coarsePointer.value) return

  if (holding.value) {
    endHold()
    swallowNextClick = true
    return
  }

  cancelHold()
  if (touchMoved || event.changedTouches.length !== 1) return

  const now = Date.now()
  const touch = event.changedTouches[0]

  const el = videoRef.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  const zone = (touch.clientX - rect.left) / rect.width
  const side = zone < 1 / 3 ? 'back' : zone > 2 / 3 ? 'forward' : null

  // Серія вже йде — кожен тап по тому самому боці додає ще 10 с, без
  // другого тапу в парі
  if (side && seekFlash.value?.direction === side && now < seekStreakUntil) {
    flashSeek(side)
    lastTapAt = 0
    swallowNextClick = true
    return
  }

  const isDouble =
    now - lastTapAt < DOUBLE_TAP_WITHIN && Math.abs(touch.clientX - lastTapX) < TAP_SLOP * 3
  lastTapAt = now
  lastTapX = touch.clientX
  if (!isDouble || !side) return

  flashSeek(side)

  lastTapAt = 0
  swallowNextClick = true
}

// Довге натискання на <video> в Android Chrome відкриває системне меню
// («Завантажити відео») — рівно на жесті прискорення
const onVideoContextmenu = (event) => {
  if (coarsePointer.value) event.preventDefault()
}

onMounted(() => {
  try {
    coarsePointer.value = window.matchMedia('(hover: none) and (pointer: coarse)').matches
  } catch (e) {}
})

onBeforeUnmount(() => {
  cancelHold()
  if (seekFlashTimer) clearTimeout(seekFlashTimer)
})

// --- Листання серій з панелі ---
// Потрібне насамперед у фулскріні: кнопки серій під плеєром звідти не видно
const hasPrevEpisode = computed(() => current.value > 0)
const hasNextEpisode = computed(
  () => current.value < (props.anime?.episodes?.length || 0) - 1
)
const prevEpisode = () => {
  if (hasPrevEpisode.value) changeEpisode(current.value - 1, true)
}
const nextEpisode = () => {
  if (hasNextEpisode.value) changeEpisode(current.value + 1, true)
}

// --- Кадри під курсором на смузі перемотки ---
// Тільки миша й тільки відкриті записи (приватним потрібен токен у кожному
// запиті сегмента, а друге приховане відео про нього не знає)
const thumbSource = computed(() =>
  props.anime?.privat ? null : props.anime?.episodes?.[current.value]?.hls || null
)
const { thumbnail: hoverFrame, request: requestFrame } = useScrubberThumbnails(
  thumbSource,
  computed(() => !coarsePointer.value)
)

// --- NijiLucid (WebGPU) — другий рушій апскейлу поруч з Anime4K.js ---
//
// Anime4K.js (WebGL) лишається як був — з тими самими режимами. NijiLucid —
// важчі нейромережеві моделі (CuNNy, ARNet, ArtCNN) і режими сумісності
// Anime4K на WebGPU, з рівнем продуктивності, тестом GPU й монітором
// продуктивності. Працює одночасно тільки один: увімкнули один — інший
// гасне (див. watch нижче)
const nijiSupported = ref(false)
const niji = ref({ ...NIJI_DEFAULTS })
const upscaleEngine = ref('anime4k') // 'anime4k' | 'nijilucid'
const nijiCanvasRef = ref(null)
const nijiRenderer = shallowRef(null)
const nijiHasFrame = ref(false)
const nijiStatus = ref('idle') // idle | benchmark | starting | running | error
const nijiProgress = ref(null)
const nijiError = ref(null)
const nijiHud = shallowRef(null)
let nijiRun = 0

const nijiActive = computed(() => nijiSupported.value && niji.value.enabled)
const nijiShowing = computed(() => nijiActive.value && nijiHasFrame.value)
const upscaleOn = computed(() => shaderActive.value || nijiActive.value)

const disableUpscale = () => {
  shaderMode.value = 'off'
  niji.value.enabled = false
}

const nijiSource = () => {
  const video = videoRef.value
  if (!video || video.videoWidth <= 0 || video.videoHeight <= 0) return null
  return { width: video.videoWidth, height: video.videoHeight }
}

const nijiSelectedMode = () => {
  const engine = getNijiEngine()
  const modes = engine.buildEnhancementModes([])
  return engine.findModeById(modes, niji.value.modeId) ?? modes[0]
}

const waitForPaintedFrame = (video) =>
  new Promise((resolve) => {
    let done = false
    const finish = () => {
      if (done) return
      done = true
      resolve()
    }
    video.requestVideoFrameCallback(finish)
    setTimeout(finish, 1500)
  })

const nijiProgressLabel = (stage, current, total) => {
  if (stage === 'init-gpu') return 'Ініціалізація GPU…'
  if (stage === 'loading-effect') return `Завантаження ефекту ${current ?? 0}/${total ?? 0}…`
  return null
}

const destroyNiji = () => {
  nijiRun += 1
  nijiHasFrame.value = false
  nijiHud.value = null
  nijiProgress.value = null
  if (nijiRenderer.value) {
    nijiRenderer.value.destroy()
    nijiRenderer.value = null
  }
  if (nijiStatus.value !== 'error') nijiStatus.value = 'idle'
}

const setupNiji = async () => {
  destroyNiji()
  const run = nijiRun
  if (!nijiActive.value) return

  const video = videoRef.value
  const canvas = nijiCanvasRef.value
  if (!video || !canvas) return

  nijiStatus.value = 'starting'
  nijiError.value = null

  const engine = await ensureNijiEngine()
  if (run !== nijiRun) return
  if (!engine) {
    niji.value.enabled = false
    nijiSupported.value = false
    return
  }

  // Розміри кадру відомі тільки після метаданих — тоді нас запустить
  // onNijiGeometryChange
  const source = nijiSource()
  if (!source) {
    nijiStatus.value = 'idle'
    return
  }

  await waitForPaintedFrame(video)
  if (run !== nijiRun) return

  const state = engine.resolveRendererState(
    nijiSelectedMode(),
    niji.value.tier,
    niji.value.resolution,
    source
  )
  canvas.width = state.targetDimensions.width
  canvas.height = state.targetDimensions.height

  try {
    const created = await engine.Renderer.create({
      video,
      canvas,
      effects: state.effects,
      effectsSignature: state.effectsSignature,
      targetDimensions: state.targetDimensions,
      performanceMonitorMode: niji.value.monitor,
      performanceModeName: nijiModeLabel(niji.value.modeId),
      performanceTier: niji.value.tier,
      onPerformanceSnapshot: (snapshot) => {
        if (run === nijiRun) nijiHud.value = snapshot
      },
      onFirstFrameRendered: () => {
        if (run === nijiRun) nijiHasFrame.value = true
      },
      onProgress: (stage, cur, total) => {
        if (run === nijiRun) nijiProgress.value = nijiProgressLabel(stage, cur, total)
      },
      onError: (err) => {
        if (run !== nijiRun) return
        console.error('[player] NijiLucid зупинився', err)
        nijiError.value = err.message
        nijiStatus.value = 'error'
        niji.value.enabled = false
      }
    })
    if (run !== nijiRun) {
      created.destroy()
      return
    }
    nijiRenderer.value = created
    nijiStatus.value = 'running'
  } catch (err) {
    if (run !== nijiRun) return
    console.error('[player] NijiLucid не запустився', err)
    nijiError.value = err instanceof Error ? err.message : String(err)
    nijiStatus.value = 'error'
    niji.value.enabled = false
  } finally {
    if (run === nijiRun) nijiProgress.value = null
  }
}

// Налаштування змінились у робочого рендерера — перебудовуємо ланцюжок на
// місці, без нового GPU-девайса. Сюди ж приходить зміна розміру кадру
// (hls.js перемкнув якість)
const reconfigureNiji = async () => {
  const engine = getNijiEngine()
  const current = nijiRenderer.value
  const canvas = nijiCanvasRef.value
  const source = nijiSource()
  if (!engine || !current || !canvas || !source) return

  const state = engine.resolveRendererState(
    nijiSelectedMode(),
    niji.value.tier,
    niji.value.resolution,
    source
  )
  if (
    canvas.width !== state.targetDimensions.width ||
    canvas.height !== state.targetDimensions.height
  ) {
    canvas.width = state.targetDimensions.width
    canvas.height = state.targetDimensions.height
  }

  try {
    await current.updateConfiguration({
      effects: state.effects,
      effectsSignature: state.effectsSignature,
      targetDimensions: state.targetDimensions,
      sourceDimensions: source
    })
    syncNijiMonitor()
  } catch (err) {
    console.error('[player] NijiLucid не перебудувався', err)
    nijiError.value = err instanceof Error ? err.message : String(err)
    nijiStatus.value = 'error'
    niji.value.enabled = false
  }
}

const syncNijiMonitor = () => {
  const current = nijiRenderer.value
  const canvas = nijiCanvasRef.value
  const source = nijiSource()
  if (!current || !canvas || !source) return
  const mode = niji.value.monitor
  const run = nijiRun
  current.updatePerformanceMonitor({
    mode,
    modeName: nijiModeLabel(niji.value.modeId),
    tier: niji.value.tier,
    sourceDimensions: source,
    targetDimensions: { width: canvas.width, height: canvas.height },
    onSnapshot:
      mode === 'off'
        ? undefined
        : (snapshot) => {
            if (run === nijiRun) nijiHud.value = snapshot
          }
  })
  if (mode === 'off') nijiHud.value = null
}

const onNijiGeometryChange = () => {
  if (!nijiActive.value) return
  if (nijiRenderer.value) reconfigureNiji()
  else if (nijiStatus.value === 'idle') setupNiji()
}

// Тест GPU: підбирає рівень продуктивності під залізо. Тільки на кнопку —
// сам не запускається: він на кілька секунд забирає відеокарту
const runNijiBenchmark = async () => {
  if (!nijiSupported.value) return
  const wasRunning = Boolean(nijiRenderer.value)
  destroyNiji()

  const engine = await ensureNijiEngine()
  if (!engine) return
  nijiStatus.value = 'benchmark'
  nijiProgress.value = 'Тест GPU… 0%'
  try {
    const result = await engine.runGPUBenchmark((progress) => {
      nijiProgress.value = `Тест GPU… ${Math.round(progress.progress * 100)}%`
    })
    niji.value.benchmark = result
    niji.value.tier = result.tier
  } catch (err) {
    console.warn('[player] тест GPU не вдався', err)
  } finally {
    nijiProgress.value = null
    if (nijiStatus.value === 'benchmark') nijiStatus.value = 'idle'
  }
  if (wasRunning && niji.value.enabled) setupNiji()
}

watch(
  () => niji.value.enabled,
  (enabled) => {
    if (enabled) {
      // Два апскейли одночасно — це подвійна робота GPU і каша на екрані
      if (shaderMode.value !== 'off') shaderMode.value = 'off'
      nijiStatus.value = 'idle'
      setupNiji()
    } else {
      destroyNiji()
      if (!shaderActive.value) compareMode.value = false
    }
  }
)

watch(shaderMode, (mode) => {
  if (mode !== 'off' && niji.value.enabled) niji.value.enabled = false
})

watch(
  () => [niji.value.modeId, niji.value.tier, niji.value.resolution],
  () => {
    if (nijiRenderer.value) reconfigureNiji()
  }
)

watch(() => niji.value.monitor, syncNijiMonitor)

watch(niji, (value) => saveNijiSettings(value), { deep: true })

watch(upscaleEngine, (value) => {
  try {
    localStorage.setItem('upscale_engine', value)
  } catch (e) {}
})

onMounted(() => {
  // На телефонах і планшетах (дотик без миші) — тільки Anime4K: важкі
  // WebGPU-моделі там гріють і садять батарею, а на маленькому екрані
  // різниця з Anime4K однаково не видна
  let touchOnly = false
  try {
    touchOnly = window.matchMedia('(hover: none) and (pointer: coarse)').matches
  } catch (e) {}
  nijiSupported.value = !touchOnly && checkNijiSupport()
  if (!nijiSupported.value) return
  niji.value = loadNijiSettings()
  try {
    const saved = localStorage.getItem('upscale_engine')
    upscaleEngine.value =
      saved === 'nijilucid' || saved === 'anime4k'
        ? saved
        : niji.value.enabled
          ? 'nijilucid'
          : 'anime4k'
  } catch (e) {}
  // Збережене «увімкнено» піднімаємо, коли в відео будуть розміри кадру
  if (niji.value.enabled && videoRef.value?.videoWidth) setupNiji()
})

onBeforeUnmount(destroyNiji)

const nijiTierOptions = NIJI_TIER_OPTIONS
const nijiModeGroups = NIJI_MODE_GROUPS
const nijiResolutionOptions = NIJI_RESOLUTION_OPTIONS
const nijiMonitorOptions = NIJI_MONITOR_OPTIONS
</script>

<template>
  <div v-if="anime">
    <div
      v-if="anime.episodes.length > 0"
      class="transition"
      :class="{
        'fixed left-0 top-0 mt-0 z-99 transition-all w-screen h-screen':
          isFullscreen,
        'lg:rounded-tl-[0px]': !isFullscreen
      }"
    >
      <div
        class="relative outline-none watchPlayer"
        :class="{ 'h-screen w-screen': isFullscreen }"
        :tabindex="0"
        autofocus
        ref="playerRef"
        @mousemove="showControls"
        @mouseleave="onPlayerMouseleave"
        @touchstart.passive="onPlayerTouchstart"
      >
        <div
          class="z-[100] fixed flex bg-base-300 p-[2px] rounded-[12px] resize cursor-move"
          v-if="chatPipActive && chatShow"
          ref="chatElRef"
          :style="[
            draggableStyle,
            {
              width: chatSize + '%',
              height: chatHeight + '%',
              '--tw-bg-opacity': chatOpacity / 100
            }
          ]"
        >
          <div
            v-if="anime.episodes[current]?.chat != undefined"
            ref="chatPipRef"
            class="right-0 relative flex flex-col p-[10px] w-full overflow-x-hidden overflow-y-auto transition-all chat"
          >
            <div
              v-for="msg in visibleChat"
              :key="msg.timesec + msg.author"
              v-memo="[msg.timesec, msg.text]"
              class="block w-full"
            >
              <span
                class="font-bold cursor-pointer"
                :style="{ color: msg.color }"
                >{{ msg.author }}</span
              >:
              <EmoteText
                class="text-white"
                :text="msg.text"
              />
            </div>
          </div>
        </div>

        <div
          class="relative flex items-center bg-black shadow rounded-[12px] overflow-hidden"
          :class="{
            'h-screen w-screen rounded-none max-h-[unset] min-h-[100vh]':
              isFullscreen,
            'aspect-video min-h-[50vh] w-full lg:rounded-tl-[0px]':
              !isFullscreen
          }"
        >
          <div class="relative flex w-full h-full">
            <div
              v-if="!fisrtPlay"
              class="lg:hidden top-0 left-0 z-[10] absolute flex justify-center items-center bg-black opacity-50 w-full h-full"
              :style="{ width: chatPanelOpen ? `${100 - chatSize}%` : '100%' }"
              @click="fisrtPlayStart"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
                aria-hidden="true"
                role="img"
                class="iconify iconify--ic"
                width="32"
                height="32"
                preserveAspectRatio="xMidYMid meet"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M8 6.82v10.36c0 .79.87 1.27 1.54.84l8.14-5.18a1 1 0 0 0 0-1.69L9.54 5.98A.998.998 0 0 0 8 6.82z"
                ></path>
              </svg>
            </div>

            <div
              class="top-[50%] left-[50%] z-10 absolute flex flex-col gap-[20px] p-[10px] w-full font-bold text-error text-lg -translate-x-[50%] -translate-y-[50%] transform"
              v-if="failed"
            >
              <div class="">
                {{ $t('failed') }}
              </div>
              <div
                class="flex items-center gap-[10px] bg-error px-[10px] py-[5px] rounded-[12px] w-[fit-content] text-white cursor-pointer"
                v-if="anime.episodes[current].tgLink"
                @click="openLink(anime.episodes[current].tgLink)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  xmlns:xlink="http://www.w3.org/1999/xlink"
                  aria-hidden="true"
                  role="img"
                  class="iconify iconify--ic"
                  width="24"
                  height="24"
                  preserveAspectRatio="xMidYMid meet"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="currentColor"
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19c-.14.75-.42 1-.68 1.03c-.58.05-1.02-.38-1.58-.75c-.88-.58-1.38-.94-2.23-1.5c-.99-.65-.35-1.01.22-1.59c.15-.15 2.71-2.48 2.76-2.69a.2.2 0 0 0-.05-.18c-.06-.05-.14-.03-.21-.02c-.09.02-1.49.95-4.22 2.79c-.4.27-.76.41-1.08.4c-.36-.01-1.04-.2-1.55-.37c-.63-.2-1.12-.31-1.08-.66c.02-.18.27-.36.74-.55c2.92-1.27 4.86-2.11 5.83-2.51c2.78-1.16 3.35-1.36 3.73-1.36c.08 0 .27.02.39.12c.1.08.13.19.14.27c-.01.06.01.24 0 .38z"
                  ></path>
                </svg>
                <span>{{ $t('telegramlink') }}</span>
              </div>
            </div>
            <div
              class="relative h-full"
              :class="{
                'min-w-[70%]': !anime.episodes[current]?.chat == undefined
              }"
              :style="{
                width: `${chatPanelOpen ? 100 - chatSize : 100}%`
              }"
              ref="compareContainerRef"
            >
              <!-- Основне відео: завжди видиме (в compare-режимі слугує лівою "оригінальною" стороною) -->
              <video
                ref="videoRef"
                class="block w-full h-full object-contain transition-all"
                :class="{ 'opacity-0': (shaderActive || nijiShowing) && !compareMode }"
                crossorigin="anonymous"
                preload="auto"
                playsinline
                :loop="loop"
                @ended="onEpisodeEnded"
                @click="onVideoClick"
                @dblclick="onVideoDblclick"
                @touchstart.passive="onVideoTouchstart"
                @touchmove.passive="onVideoTouchmove"
                @touchend="onVideoTouchend"
                @touchcancel="endHold"
                @contextmenu="onVideoContextmenu"
                @loadedmetadata="onNijiGeometryChange"
                @loadeddata="detectPipSupport"
                @resize="onNijiGeometryChange"
                x-webkit-airplay="allow"
              />
              <!-- Канвас NijiLucid (WebGPU): рендерер малює в нього одразу в
                   цільовій роздільності, браузер вписує його в кадр так само,
                   як <video>. У режимі порівняння обрізаний по розділювачу -->
              <canvas
                v-if="nijiSupported"
                v-show="nijiShowing"
                ref="nijiCanvasRef"
                class="block top-0 left-0 absolute w-full h-full object-contain pointer-events-none"
                :style="
                  compareMode && nijiShowing
                    ? { clipPath: `inset(0 0 0 ${compareDividerPct}%)` }
                    : {}
                "
              />
              <!-- Прихований канвас: у нього рендерить сама бібліотека anime4k.js -->
              <canvas
                v-if="shaderSupported"
                ref="shaderCanvasRef"
                class="hidden"
              />
              <!-- Видимий канвас апскейлу:
                   - звичайний режим: накриває все відео повністю
                   - compare-режим: обрізаний з лівого боку до позиції розділювача -->
              <canvas
                v-if="shaderSupported"
                ref="shaderDisplayCanvasRef"
                v-show="shaderActive"
                class="block top-0 left-0 absolute w-full h-full object-contain pointer-events-none"
                :style="
                  compareMode && shaderActive
                    ? { clipPath: `inset(0 0 0 ${compareDividerPct}%)` }
                    : {}
                "
              />

              <!-- Compare-режим: розділювач та підписи (для обох рушіїв) -->
              <template v-if="compareMode && (shaderActive || nijiShowing)">
                <!-- Розділювач -->
                <div
                  class="top-0 z-20 absolute flex justify-center items-center h-full cursor-ew-resize select-none"
                  :style="{
                    left: `calc(${compareDividerPct}% - 1px)`,
                    width: '3px'
                  }"
                  @mousedown="onDividerMousedown"
                  @touchstart.passive="onDividerTouchstart"
                >
                  <div class="bg-white/90 shadow-lg w-[3px] h-full" />
                  <!-- Ручка -->
                  <div
                    class="top-1/2 left-1/2 z-30 absolute flex justify-center items-center bg-white shadow-lg rounded-full w-8 h-8 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M8 5l-5 7 5 7M16 5l5 7-5 7"
                        stroke="#333"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </div>
                <!-- Підписи -->
                <div
                  class="top-3 left-3 z-20 absolute bg-black/60 px-2 py-0.5 rounded text-white text-xs pointer-events-none select-none"
                >
                  Оригінал
                </div>
                <div
                  class="top-3 right-3 z-20 absolute bg-black/60 px-2 py-0.5 rounded text-white text-xs pointer-events-none select-none"
                >
                  Апскейл
                </div>
              </template>
            </div>
            <!--
              Режим кінотеатру: панель чату праворуч від відео. Джерело
              перемикається в меню — чат сайту або архів стріму.
            -->
            <div
              ref="chatContainerRef"
              class="right-0 relative flex flex-col transition-all chat"
              :class="isFrameChat ? 'overflow-hidden' : 'p-[10px] overflow-x-hidden overflow-y-auto'"
              :style="{
                width: `${chatPanelOpen ? chatSize : 0}%`,
                display: `${chatPanelOpen ? 'flex' : 'none'}`
              }"
            >
              <!-- Чат сайту — та сама гілка і та сама шапка, що й у вікна,
                   тільки без прозорості: панель нічого не перекриває -->
              <template v-if="isFrameChat">
                <ChatToolbar
                  v-model:zoom="panelZoom"
                  @close="chatShow = false"
                />
                <div class="relative flex-1 overflow-hidden">
                  <div
                    class="flex w-full h-full"
                    :style="
                      panelZoom === 1
                        ? {}
                        : {
                            width: 100 / panelZoom + '%',
                            height: 100 / panelZoom + '%',
                            transform: `scale(${panelZoom})`,
                            transformOrigin: 'top left'
                          }
                    "
                  >
                    <ChatBody
                      :page-id="siteChatConfig.pageId"
                      :page-title="siteChatConfig.pageTitle"
                      :page-url="siteChatConfig.pageUrl"
                    />
                  </div>
                </div>
              </template>

              <!-- Архів чату стріму, синхронний з таймкодом -->
              <template v-else>
                <div
                  v-for="msg in visibleChat"
                  :key="msg.timesec + msg.text"
                  v-memo="[msg.timesec, msg.text]"
                  class="block w-full"
                >
                  <span
                    class="font-bold"
                    :style="{ color: msg.color }"
                    >{{ msg.author }}</span
                  >:
                  <EmoteText
                    class="text-white"
                    :text="msg.text"
                  />
                </div>
              </template>
            </div>
          </div>

          <div
            v-if="waiting"
            class="absolute inset-0 place-items-center grid bg-black bg-opacity-20 pointer-events-none"
          >
            <VideoSpinner />
          </div>

          <!-- Підказки жестів на телефоні: «2×», поки тримають палець, і
               «−10 с» / «+10 с» біля того краю, де тапнули двічі -->
          <div
            v-if="holding"
            class="top-3 z-20 absolute inset-x-0 flex justify-center pointer-events-none"
          >
            <span class="bg-black/70 px-3 py-1 rounded-full font-semibold text-white text-sm">
              {{ HOLD_RATE }}×
            </span>
          </div>
          <div
            v-if="seekFlash"
            class="z-20 absolute inset-y-0 flex items-center px-6 pointer-events-none"
            :class="seekFlash.direction === 'back' ? 'left-0' : 'right-0'"
          >
            <span class="bg-black/70 px-3 py-1.5 rounded-full font-semibold tabular-nums text-white text-sm">
              {{ seekFlash.direction === 'back' ? `−${seekFlash.total} с` : `+${seekFlash.total} с` }}
            </span>
          </div>

          <!-- Що зараз робить NijiLucid: завантаження моделей, тест GPU -->
          <div
            v-if="nijiActive && nijiProgress"
            class="top-3 left-3 z-20 absolute flex items-center gap-2 bg-black/70 px-2 py-1 rounded text-white text-xs pointer-events-none"
          >
            <span class="loading loading-spinner loading-xs" />
            {{ nijiProgress }}
          </div>

          <!-- Монітор продуктивності NijiLucid -->
          <VideoShaderHud
            v-if="nijiActive && niji.monitor !== 'off' && nijiHud"
            :snapshot="nijiHud"
            :collapsed="niji.hudCollapsed"
            :position="niji.hudPosition"
            :width="niji.hudWidth"
            @update:collapsed="niji.hudCollapsed = $event"
            @update:position="niji.hudPosition = $event"
            @update:width="niji.hudWidth = $event"
            @close="niji.monitor = 'off'"
          />

          <!-- Дебаг-інформація (роздільність, шейдер, буфер, HLS) -->
          <div
            v-if="showDebugInfo && debugStats"
            class="top-2 left-2 z-20 absolute bg-black/70 p-2 rounded font-mono text-[11px] text-success leading-tight whitespace-pre pointer-events-none"
          >
            <div>Відео: {{ debugStats.videoRes }}</div>
            <div>
              Канвас шейдера (прихований): {{ debugStats.shaderCanvasRes }}
            </div>
            <div>
              Канвас показу (видимий): {{ debugStats.displayCanvasRes }}
            </div>
            <div>Шейдер: {{ debugStats.shaderMode }}</div>
            <div v-if="debugStats.shaderActive">
              Якість: {{ debugStats.shaderQuality }}
            </div>
            <div v-if="debugStats.shaderActive">
              Роздільність шейдера: {{ debugStats.shaderScale }}
            </div>
            <div>
              WebGL підтримка: {{ debugStats.shaderSupported ? 'так' : 'ні' }}
            </div>
            <div>
              Час: {{ debugStats.currentTime }} / {{ debugStats.duration }}
            </div>
            <div>Буфер попереду: {{ debugStats.bufferedAhead }}</div>
            <div>Пропущені кадри: {{ debugStats.droppedFrames }}</div>
            <div>Швидкість: {{ debugStats.playbackRate }}</div>
            <div>
              readyState/networkState: {{ debugStats.readyState }}/{{
                debugStats.networkState
              }}
            </div>
            <template v-if="debugStats.hls">
              <div>Рушій: {{ debugStats.hls.engine }}</div>
              <div v-if="debugStats.hls.engine === 'hls.js'">
                Рівень: {{ debugStats.hls.level }} ({{
                  debugStats.hls.bitrate
                }})
              </div>
              <div v-if="debugStats.hls.engine === 'hls.js'">
                Оцінка каналу: {{ debugStats.hls.bandwidthEstimate }}
              </div>
            </template>
          </div>

          <client-only>
            <!--
              Панель керування — як у плеєрі shiki.sgezha.art: поверх кадру,
              на градієнті знизу, смуга перемотки згори й рівно один ряд кнопок.
              Усе, що не влазить на вузькому екрані, ховається по брейкпоінтах,
              а не переноситься другим рядком — він ріс би вгору й закривав кадр.

              Панель не зникає з DOM, а тільки стає прозорою: меню всередині
              інакше закривалися б разом із нею.

              Лежить у контейнері, який ділять відео і чат, тож у режимі
              кінотеатру обрізаємо її по ширині відео — інакше вона накриває
              низ чату разом з полем вводу
            -->
            <div
              class="bottom-0 left-0 z-10 absolute bg-gradient-to-t from-black/90 via-black/50 to-transparent px-2 sm:px-3 lg:px-4 pt-10 pb-2 sm:pb-3 w-[100%] transition-opacity duration-200 video-menu"
              :class="{ 'opacity-0 pointer-events-none': !controlsVisible }"
              :style="{
                width: limitControlsWidth ? `${100 - chatSize}%` : '100%'
              }"
            >
                <!-- Смуга перемотки: час — рівно над курсором, кадр — вище.
                     Перемотуємо один раз, коли відпустили: кожен запис у
                     currentTime — це справжня перемотка з новими сегментами -->
                <VideoScrubber
                  v-model="currentTime"
                  :max="duration || 1"
                  :buffered-ranges="buffered"
                  commit-on-release
                  class="mb-2"
                  @hover="requestFrame"
                >
                  <template #default="{ position, pendingValue }">
                    <img
                      v-if="hoverFrame"
                      :src="hoverFrame"
                      class="bottom-11 absolute bg-black shadow-lg border border-white/20 rounded w-40 aspect-video object-cover -translate-x-1/2 pointer-events-none"
                      :style="{ left: `clamp(5rem, ${position}, calc(100% - 5rem))` }"
                      alt=""
                    />
                    <div
                      class="bottom-4 absolute bg-neutral shadow px-2 py-1 rounded text-neutral-content text-xs -translate-x-1/2 pointer-events-none"
                      :style="{ left: position }"
                    >
                      {{ formatDuration(pendingValue) }}
                    </div>
                  </template>
                </VideoScrubber>

                <div class="flex flex-nowrap items-center gap-0.5 sm:gap-1">
                  <!-- Листання серій — обабіч «грати», як у будь-якому плеєрі.
                       Потрібне насамперед у фулскріні -->
                  <!-- На найвужчих телефонах (<360px) листання ховаємо: інакше ряд
                       не влазить, а серії є в списку під плеєром -->
                  <button
                    v-if="anime.episodes.length > 1"
                    class="max-[359px]:hidden btn videoBtn"
                    :disabled="!hasPrevEpisode"
                    title="Попередня серія (Shift+P)"
                    @click="prevEpisode"
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
                    </svg>
                  </button>

                  <button
                    class="btn videoBtn"
                    :title="playing ? 'Пауза (K)' : 'Грати (K)'"
                    @click="playing = !playing"
                  >
                    <svg
                      v-if="!playing"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                      aria-hidden="true"
                      role="img"
                      class="iconify iconify--ic"
                      width="32"
                      height="32"
                      preserveAspectRatio="xMidYMid meet"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M8 6.82v10.36c0 .79.87 1.27 1.54.84l8.14-5.18a1 1 0 0 0 0-1.69L9.54 5.98A.998.998 0 0 0 8 6.82z"
                      ></path>
                    </svg>
                    <svg
                      v-else
                      xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                      aria-hidden="true"
                      role="img"
                      class="iconify iconify--ic"
                      width="32"
                      height="32"
                      preserveAspectRatio="xMidYMid meet"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M8 19c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2s-2 .9-2 2v10c0 1.1.9 2 2 2zm6-12v10c0 1.1.9 2 2 2s2-.9 2-2V7c0-1.1-.9-2-2-2s-2 .9-2 2z"
                      ></path>
                    </svg>
                  </button>
                  <button
                    v-if="anime.episodes.length > 1"
                    class="max-[359px]:hidden btn videoBtn"
                    :disabled="!hasNextEpisode"
                    title="Наступна серія (Shift+N)"
                    @click="nextEpisode"
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
                    </svg>
                  </button>

                  <!-- ±10 с. На телефоні це подвійний тап біля краю кадру, а в
                       ряду кнопкам не лишалось місця поруч із листанням серій -->
                  <button
                    class="hidden sm:inline-flex btn videoBtn"
                    title="Назад 10 секунд (J)"
                    @click="seekBy(-10)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M3 2.25a1 1 0 0 0-1 1V9a1 1 0 0 0 1 1h5.25a1 1 0 0 0 0-2H4.863c.985-1.179 2.208-2.113 3.682-2.57a9.435 9.435 0 0 1 6.106.16c1.981.738 3.81 2.232 4.722 3.894a1 1 0 1 0 1.754-.961c-1.175-2.143-3.415-3.927-5.778-4.807a11.435 11.435 0 0 0-7.395-.196C6.378 4.008 5.068 4.9 4 5.979V3.25a1 1 0 0 0-1-1M9.75 12a1 1 0 0 0-1.762-.647l-.222.265c-.515.623-1.078 1.303-2.28 2.024a1 1 0 1 0 1.029 1.716c.483-.29.889-.582 1.235-.865V20a1 1 0 0 0 2 0zm6.5-1c-1.386 0-2.4.635-3.008 1.661c-.564.95-.742 2.161-.742 3.339s.178 2.39.742 3.339C13.85 20.365 14.864 21 16.25 21s2.4-.635 3.008-1.661c.564-.95.742-2.161.742-3.339s-.178-2.39-.742-3.339C18.65 11.635 17.636 11 16.25 11m-1.75 5c0-1.031.166-1.82.461-2.318c.251-.422.613-.682 1.289-.682c.676 0 1.038.26 1.288.682c.296.498.462 1.287.462 2.318c0 1.031-.166 1.82-.462 2.318c-.25.422-.612.682-1.288.682s-1.038-.26-1.289-.682c-.295-.498-.461-1.287-.461-2.318"
                      />
                    </svg>
                  </button>
                  <button
                    class="hidden sm:inline-flex btn videoBtn"
                    title="Вперед 10 секунд (L)"
                    @click="seekBy(10)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M21 2.25a1 1 0 0 1 1 1V9a1 1 0 0 1-1 1h-5.25a1 1 0 0 1 0-2h3.387c-.985-1.179-2.208-2.113-3.682-2.57a9.435 9.435 0 0 0-6.106.16c-1.981.738-3.81 2.232-4.722 3.894a1 1 0 0 1-1.754-.961C4.048 6.38 6.288 4.596 8.652 3.716a11.435 11.435 0 0 1 7.394-.196c1.576.488 2.886 1.38 3.954 2.459V3.25a1 1 0 0 1 1-1M9.095 11.061A1 1 0 0 1 9.75 12v8a1 1 0 0 1-2 0v-5.507c-.346.283-.752.575-1.235.864a1 1 0 1 1-1.03-1.715c1.203-.721 1.766-1.4 2.281-2.024c.074-.09.148-.178.222-.265a1 1 0 0 1 1.107-.292m4.147 1.6C13.85 11.635 14.864 11 16.25 11s2.4.635 3.008 1.661c.564.95.742 2.161.742 3.339s-.178 2.39-.742 3.339C18.65 20.365 17.636 21 16.25 21s-2.4-.636-3.008-1.661c-.564-.95-.742-2.161-.742-3.339s.178-2.39.742-3.339m1.72 1.021c-.296.498-.462 1.287-.462 2.318c0 1.031.166 1.82.461 2.318c.251.422.613.682 1.289.682c.676 0 1.038-.26 1.288-.682c.296-.498.462-1.287.462-2.318c0-1.031-.166-1.82-.462-2.318c-.25-.422-.612-.682-1.288-.682s-1.038.26-1.289.682"
                      />
                    </svg>
                  </button>
                  <button
                    class="hidden sm:inline-flex btn videoBtn"
                    title="Звук (M, колесо — гучність)"
                    @click="muted = !muted"
                    @wheel.prevent="onVolumeWheel"
                  >
                    <svg
                      v-if="muted"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                      aria-hidden="true"
                      role="img"
                      class="iconify iconify--ic"
                      width="32"
                      height="32"
                      preserveAspectRatio="xMidYMid meet"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M3.63 3.63a.996.996 0 0 0 0 1.41L7.29 8.7L7 9H4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71v-4.17l4.18 4.18c-.49.37-1.02.68-1.6.91c-.36.15-.58.53-.58.92c0 .72.73 1.18 1.39.91c.8-.33 1.55-.77 2.22-1.31l1.34 1.34a.996.996 0 1 0 1.41-1.41L5.05 3.63c-.39-.39-1.02-.39-1.42 0zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87c0-3.83-2.4-7.11-5.78-8.4c-.59-.23-1.22.23-1.22.86v.19c0 .38.25.71.61.85C17.18 6.54 19 9.06 19 12zm-8.71-6.29l-.17.17L12 7.76V6.41c0-.89-1.08-1.33-1.71-.7zM16.5 12A4.5 4.5 0 0 0 14 7.97v1.79l2.48 2.48c.01-.08.02-.16.02-.24z"
                      ></path>
                    </svg>
                    <svg
                      v-else
                      xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                      aria-hidden="true"
                      role="img"
                      class="iconify iconify--ic"
                      width="32"
                      height="32"
                      preserveAspectRatio="xMidYMid meet"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
                      ></path>
                    </svg>
                  </button>
                  <VideoScrubber
                    v-model="volume"
                    :max="1"
                    class="hidden sm:flex ml-1 w-16 md:w-20 lg:w-28"
                    title="Гучність (колесо теж працює)"
                    @wheel.prevent="onVolumeWheel"
                  />

                  <span
                    class="flex-none ml-1 sm:ml-2 tabular-nums text-[11px] text-white sm:text-xs whitespace-nowrap"
                  >
                    {{ formatDuration(currentTime) }}
                    <span class="max-[379px]:hidden opacity-60">/ {{ formatDuration(duration) }}</span>
                  </span>

                  <div class="flex-1 min-w-0"></div>

                  <!-- Кліп: вирізати момент серії (див. components/clip/Editor).
                       На телефоні — пунктом у меню налаштувань: ряд кнопок
                       там і так щільний -->
                  <button
                    v-if="canClip"
                    class="hidden sm:inline-flex items-center gap-1 btn videoBtn"
                    title="Зробити кліп"
                    @click="openClipEditor"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <circle cx="6" cy="6" r="3" />
                      <circle cx="6" cy="18" r="3" />
                      <path d="M20 4 8.12 15.88M14.47 14.48 20 20M8.12 8.12 12 12" />
                    </svg>
                    <span class="hidden xl:inline font-medium text-sm normal-case">Кліп</span>
                  </button>

                  <!-- Кнопка чату є завжди: архіву може не бути, але Twitch
                       і чат сайту доступні на будь-якій серії -->
                  <VideoChat
                    ref="chatMenuRef"
                    sheet
                    class="flex flex-col"
                  >
                    <template #default="{ toggle }">
                      <button
                        class="btn videoBtn"
                        @click="toggle()"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          xmlns:xlink="http://www.w3.org/1999/xlink"
                          aria-hidden="true"
                          role="img"
                          class="iconify iconify--carbon"
                          width="24"
                          height="24"
                          preserveAspectRatio="xMidYMid meet"
                          viewBox="0 0 32 32"
                        >
                          <path
                            fill="currentColor"
                            d="M17.74 30L16 29l4-7h6a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h9v2H6a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4h20a4 4 0 0 1 4 4v12a4 4 0 0 1-4 4h-4.84Z"
                          ></path>
                          <path
                            fill="currentColor"
                            d="M8 10h16v2H8zm0 6h10v2H8z"
                          ></path>
                        </svg>
                      </button>
                    </template>
                    <template #menu="{ close }">
                      <div
                        class="z-30 max-sm:z-[2147483004] flex flex-col gap-[10px] bg-neutral shadow-xl p-3 rounded overflow-y-auto overscroll-contain select-none max-sm:fixed max-sm:inset-x-2 max-sm:bottom-2 max-sm:max-h-[70vh] sm:absolute sm:right-0 sm:bottom-[52px] sm:max-h-[60vh] sm:w-max sm:min-w-[18rem] sm:max-w-[min(22rem,calc(100vw-1.5rem))]"
                      >
                        <VideoMenuItem>
                          <div class="flex items-center gap-[6px]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              xmlns:xlink="http://www.w3.org/1999/xlink"
                              aria-hidden="true"
                              role="img"
                              class="iconify iconify--carbon"
                              width="24"
                              height="24"
                              preserveAspectRatio="xMidYMid meet"
                              viewBox="0 0 32 32"
                            >
                              <path
                                fill="currentColor"
                                d="M17.74 30L16 29l4-7h6a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h9v2H6a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4h20a4 4 0 0 1 4 4v12a4 4 0 0 1-4 4h-4.84Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M8 10h16v2H8zm0 6h10v2H8z"
                              ></path>
                            </svg>
                            <span>{{ $t('chat') }}</span>
                          </div>
                          <input
                            class="toggle toggle-primary"
                            type="checkbox"
                            v-model="chatShow"
                          />
                        </VideoMenuItem>

                        <!-- Що показувати в панелі праворуч -->
                        <VideoMenuItem>
                          <div class="flex items-center gap-[6px]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="currentColor"
                                d="M4 6h16v2H4zm0 5h16v2H4zm0 5h10v2H4z"
                              />
                            </svg>
                            <span>Джерело</span>
                          </div>
                          <select
                            class="bg-base-300 bg-opacity-50 text-neutral-content select-sm select"
                            v-model="chatSource"
                            @change="chatSourceChosen = true"
                          >
                            <option
                              v-for="opt in chatSourceOptions"
                              :key="opt.id"
                              :value="opt.id"
                              :disabled="!opt.available"
                            >
                              {{ opt.label
                              }}{{ opt.available ? '' : ' (немає)' }}
                            </option>
                          </select>
                        </VideoMenuItem>

                        <!-- Кінотеатр (панель праворуч) або окреме вікно -->
                        <VideoMenuItem>
                          <div class="flex items-center gap-[6px]">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 32 32"
                            >
                              <path
                                fill="currentColor"
                                d="M22 4v2h4.586L20 12.586L21.414 14L28 7.414V12h2V4h-8zm6 12v4a1.996 1.996 0 0 1-2 2h-6l-4 7l1.736 1l3.429-6H26a4 4 0 0 0 4-4v-4zM4 20V8a1.996 1.996 0 0 1 2-2h12V4H6a3.999 3.999 0 0 0-4 4v12a4 4 0 0 0 4 4h9v-2H6a1.996 1.996 0 0 1-2-2z"
                              />
                            </svg>
                            <span>Режим</span>
                          </div>
                          <select
                            class="bg-base-300 bg-opacity-50 text-neutral-content select-sm select"
                            v-model="chatMode"
                          >
                            <option value="theater">Кінотеатр</option>
                            <option value="popup">Вікно</option>
                          </select>
                        </VideoMenuItem>
                        <VideoMenuItem v-if="!isPopupMode">
                          <div class="flex items-center gap-[6px]">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 21 21"
                            >
                              <path
                                fill="none"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="m15.507 14.515l4-4l-4-4.015m-10 8.015l-4-4l4-4.015m13.993 4h-18"
                              />
                            </svg>
                            <span>{{ $t('width') }}</span>
                          </div>
                          <select
                            class="bg-base-300 bg-opacity-50 text-neutral-content select-sm select"
                            v-model="chatSize"
                          >
                            <option value="10">10%</option>
                            <option value="20">20%</option>
                            <option value="30">30%</option>
                            <option value="40">40%</option>
                            <option value="50">50%</option>
                            <option value="60">60%</option>
                            <option value="70">70%</option>
                            <option value="80">80%</option>
                            <option value="90">90%</option>
                          </select>
                        </VideoMenuItem>

                        <VideoMenuItem v-if="chatPipActive">
                          <div class="flex items-center gap-[6px]">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 21 21"
                            >
                              <path
                                fill="none"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="m14.519 5.497l-4-4l-4.015 4m8.015 10l-4 4l-4.015-4m4-13.993v18"
                              />
                            </svg>
                            <span>{{ $t('height') }}</span>
                          </div>
                          <select
                            class="bg-base-300 bg-opacity-50 text-neutral-content select-sm select"
                            v-model="chatHeight"
                          >
                            <option value="10">10%</option>
                            <option value="20">20%</option>
                            <option value="30">30%</option>
                            <option value="40">40%</option>
                            <option value="50">50%</option>
                            <option value="60">60%</option>
                            <option value="70">70%</option>
                            <option value="80">80%</option>
                            <option value="90">90%</option>
                            <option value="100">100%</option>
                          </select>
                        </VideoMenuItem>
                        <VideoMenuItem v-if="chatPipActive">
                          <div class="flex items-center gap-[6px]">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="currentColor"
                                d="M18 10V8h2v2h-2m0 2v-2h-2v2h2m0-4V6h-2v2h2m-2-5.16V4h2c-.63-.46-1.29-.85-2-1.16M18 4v2h2c-.58-.75-1.25-1.42-2-2m2 2v2h1.16c-.31-.71-.7-1.37-1.16-2m2 6c0-.68-.07-1.35-.2-2H20v2h2m-6-6V4h-2v2h2m0 10h2v-2h-2v2m2 2h2v-2h-2v2m-2 2h2v-2h-2v2m-2 1.8c.7-.14 1.36-.36 2-.64V20h-2v1.8m4-7.8h2v-2h-2v2m-2-6h-2v2h2V8m4 8h1.16c.28-.64.5-1.3.64-2H20v2m-4-4h-2v2h2v-2m-4 6v-2h2v-2h-2v-2h2v-2h-2V8h2V6h-2V4h2V2.2c-.65-.13-1.31-.2-2-.2C6.5 2 2 6.5 2 12s4.5 10 10 10v-2h2v-2h-2m2 0h2v-2h-2v2Z"
                              />
                            </svg>
                            <span>{{ $t('opacity') }}</span>
                          </div>
                          <select
                            class="bg-base-300 bg-opacity-50 text-neutral-content select-sm select"
                            v-model="chatOpacity"
                          >
                            <option value="0">0%</option>
                            <option value="10">10%</option>
                            <option value="20">20%</option>
                            <option value="30">30%</option>
                            <option value="40">40%</option>
                            <option value="50">50%</option>
                            <option value="60">60%</option>
                            <option value="70">70%</option>
                            <option value="80">80%</option>
                            <option value="90">90%</option>
                            <option value="100">100%</option>
                          </select>
                        </VideoMenuItem>
                      </div>
                    </template>
                  </VideoChat>

                  <!-- Окреме меню шейдера Anime4K (тільки десктоп) -->
                  <VideoMenu
                    v-if="shaderSupported || nijiSupported"
                    ref="shaderMenuRef"
                    sheet
                    class="flex flex-col"
                  >
                    <template #default="{ toggle }">
                      <button
                        class="btn videoBtn"
                        title="Апскейл і шейдери"
                        :class="{ 'text-primary': upscaleOn }"
                        @click="toggle()"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                        >
                          <path
                            fill="currentColor"
                            d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z"
                          ></path>
                        </svg>
                      </button>
                    </template>
                    <template #menu="{ close }">
                      <div
                        class="z-30 max-sm:z-[2147483004] flex flex-col gap-[10px] bg-neutral shadow-xl p-3 rounded overflow-y-auto overscroll-contain select-none max-sm:fixed max-sm:inset-x-2 max-sm:bottom-2 max-sm:max-h-[70vh] sm:absolute sm:right-0 sm:bottom-[52px] sm:max-h-[60vh] sm:w-max sm:min-w-[18rem] sm:max-w-[min(22rem,calc(100vw-1.5rem))]"
                      >
                        <!-- Вимикач для обох рушіїв одразу. Кожен рушій має свій
                             перемикач, але видно лише розділ вибраного — і
                             апскейл іншого, увімкнений раніше, вимкнути було
                             нічим, а в Safari він лишав чорний екран -->
                        <button
                          v-if="upscaleOn"
                          class="btn btn-sm btn-error btn-outline w-full"
                          @click="disableUpscale(); close()"
                        >
                          Вимкнути апскейл
                        </button>

                        <!-- Рушій: показуємо вибір, лише коли браузер уміє обидва.
                             Anime4K.js (WebGL) — легкий і скрізь; NijiLucid
                             (WebGPU) — нейромережеві моделі, для десктопа -->
                        <label
                          v-if="nijiSupported && shaderSupported"
                          class="flex flex-col gap-1 px-1"
                        >
                          <span class="opacity-60 text-neutral-content text-xs">Рушій</span>
                          <select
                            v-model="upscaleEngine"
                            class="bg-base-300 bg-opacity-50 w-full text-neutral-content select-sm select"
                          >
                            <option value="nijilucid">NijiLucid (WebGPU, важкі моделі)</option>
                            <option value="anime4k">Anime4K (WebGL, легкий)</option>
                          </select>
                        </label>

                        <!-- NijiLucid: рівень, режим, роздільність, монітор, тест GPU -->
                        <template v-if="nijiSupported && (upscaleEngine === 'nijilucid' || !shaderSupported)">
                          <label class="flex justify-between items-center gap-2 px-1 cursor-pointer">
                            <span class="font-medium text-neutral-content text-sm">Апскейл NijiLucid</span>
                            <input
                              v-model="niji.enabled"
                              type="checkbox"
                              class="toggle toggle-primary toggle-sm"
                            />
                          </label>
                          <p class="opacity-60 px-1 -mt-2 text-neutral-content text-xs whitespace-normal">
                            Найкраще й найшвидше працює в Chrome.
                          </p>

                          <div
                            v-if="nijiProgress"
                            class="opacity-70 px-1 -mt-1 text-neutral-content text-xs"
                          >
                            {{ nijiProgress }}
                          </div>
                          <div
                            v-else-if="nijiStatus === 'error' && nijiError"
                            class="px-1 -mt-1 text-error text-xs whitespace-normal"
                          >
                            Апскейл не запустився: {{ nijiError }}
                          </div>

                          <label class="flex flex-col gap-1 px-1">
                            <span class="opacity-60 text-neutral-content text-xs">Продуктивність</span>
                            <select
                              v-model="niji.tier"
                              class="bg-base-300 bg-opacity-50 w-full text-neutral-content select-sm select"
                            >
                              <option
                                v-for="o in nijiTierOptions"
                                :key="o.value"
                                :value="o.value"
                              >
                                {{ o.label }}
                              </option>
                            </select>
                          </label>

                          <label class="flex flex-col gap-1 px-1">
                            <span class="opacity-60 text-neutral-content text-xs">Режим</span>
                            <select
                              v-model="niji.modeId"
                              class="bg-base-300 bg-opacity-50 w-full text-neutral-content select-sm select"
                            >
                              <optgroup
                                v-for="group in nijiModeGroups"
                                :key="group.label"
                                :label="group.label"
                              >
                                <option
                                  v-for="o in group.options"
                                  :key="o.value"
                                  :value="o.value"
                                >
                                  {{ o.label }}
                                </option>
                              </optgroup>
                            </select>
                          </label>

                          <label class="flex flex-col gap-1 px-1">
                            <span class="opacity-60 text-neutral-content text-xs">Роздільність</span>
                            <select
                              v-model="niji.resolution"
                              class="bg-base-300 bg-opacity-50 w-full text-neutral-content select-sm select"
                            >
                              <option
                                v-for="o in nijiResolutionOptions"
                                :key="o.value"
                                :value="o.value"
                              >
                                {{ o.label }}
                              </option>
                            </select>
                          </label>

                          <label class="flex flex-col gap-1 px-1">
                            <span class="opacity-60 text-neutral-content text-xs">Монітор продуктивності</span>
                            <select
                              v-model="niji.monitor"
                              class="bg-base-300 bg-opacity-50 w-full text-neutral-content select-sm select"
                            >
                              <option
                                v-for="o in nijiMonitorOptions"
                                :key="o.value"
                                :value="o.value"
                              >
                                {{ o.label }}
                              </option>
                            </select>
                          </label>

                          <label
                            v-if="niji.enabled"
                            class="flex justify-between items-center gap-2 px-1 text-neutral-content text-sm cursor-pointer"
                          >
                            <span>Порівняти з оригіналом</span>
                            <input
                              v-model="compareMode"
                              type="checkbox"
                              class="toggle toggle-primary toggle-sm"
                            />
                          </label>

                          <!-- Тест GPU підбирає рівень під залізо. Тільки по
                               кнопці — на кілька секунд забирає відеокарту -->
                          <div class="flex justify-between items-center gap-2 px-1 text-neutral-content text-sm">
                            <span class="flex flex-col leading-tight">
                              <span>Тест GPU</span>
                              <span
                                v-if="niji.benchmark"
                                class="opacity-50 text-[10px]"
                                >рекомендований рівень: {{ nijiTierLabel(niji.benchmark.tier) }}</span
                              >
                            </span>
                            <button
                              class="btn btn-xs btn-outline shrink-0"
                              :disabled="nijiStatus === 'benchmark' || nijiStatus === 'starting'"
                              @click="runNijiBenchmark"
                            >
                              Запустити
                            </button>
                          </div>
                        </template>

                        <!-- Anime4K.js (WebGL) — режими, як і раніше -->
                        <template v-if="shaderSupported && (upscaleEngine === 'anime4k' || !nijiSupported)">
                        <VideoMenuItem>
                          <div class="flex items-center gap-[6px]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="currentColor"
                                d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z"
                              ></path>
                            </svg>
                            <span>Шейдер</span>
                          </div>
                          <select
                            v-model="shaderMode"
                            class="bg-base-300 bg-opacity-50 w-[100px] text-neutral-content select-sm select"
                          >
                            <option
                              v-for="opt in shaderModeOptions"
                              :key="opt.value"
                              :value="opt.value"
                            >
                              {{ opt.label }}
                            </option>
                          </select>
                        </VideoMenuItem>
                        <!-- Якість шейдера -->
                        <VideoMenuItem v-if="shaderMode !== 'off'">
                          <div class="flex items-center gap-[6px]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="currentColor"
                                d="M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z"
                              ></path>
                            </svg>
                            <span>Якість</span>
                          </div>
                          <select
                            v-model="shaderQuality"
                            class="bg-base-300 bg-opacity-50 w-[100px] text-neutral-content select-sm select"
                          >
                            <option
                              v-for="opt in shaderQualityOptions"
                              :key="opt.value"
                              :value="opt.value"
                            >
                              {{ opt.label }}
                            </option>
                          </select>
                        </VideoMenuItem>
                        <!-- Роздільність шейдера (2K нативна / 4K апскейл x2) -->
                        <VideoMenuItem
                          v-if="
                            shaderMode !== 'off' && shaderMode !== 'restore'
                          "
                        >
                          <div class="flex items-center gap-[6px]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                            >
                              <!-- Icon from Google Material Icons by Material Design Authors - https://github.com/material-icons/material-icons/blob/master/LICENSE -->
                              <path
                                fill="currentColor"
                                d="M20 3H4c-1.1 0-2 .9-2 2v12a2 2 0 0 0 2 2h4v1c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-1h4c1.1 0 2-.9 2-2V5a2 2 0 0 0-2-2m0 14H4V5h16z"
                              />
                              <path
                                fill="currentColor"
                                d="M6.5 7.5h1.75c.41 0 .75-.34.75-.75S8.66 6 8.25 6H6c-.55 0-1 .45-1 1v2.25c0 .41.34.75.75.75s.75-.34.75-.75zM18.25 12c-.41 0-.75.34-.75.75v1.75h-1.75c-.41 0-.75.34-.75.75s.34.75.75.75H18c.55 0 1-.45 1-1v-2.25c0-.41-.34-.75-.75-.75"
                              />
                            </svg>
                            <span>Апскейл</span>
                          </div>
                          <select
                            v-model="shaderScale"
                            class="bg-base-300 bg-opacity-50 w-[100px] text-neutral-content select-sm select"
                          >
                            <option
                              v-for="opt in shaderScaleOptions"
                              :key="opt.value"
                              :value="opt.value"
                            >
                              {{ opt.label }}
                            </option>
                          </select>
                        </VideoMenuItem>
                        <!-- Режим порівняння (тільки коли шейдер увімкнено) -->
                        <VideoMenuItem v-if="shaderMode !== 'off'">
                          <div class="flex items-center gap-[6px]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="currentColor"
                                d="M10 20H8V4h2v16zm-4 0H4V4h2v16zM14 4h-2v16h2V4zm6 0h-2v16h2V4z"
                              ></path>
                            </svg>
                            <span>Режим порівняння</span>
                          </div>
                          <input
                            class="toggle toggle-primary"
                            type="checkbox"
                            v-model="compareMode"
                          />
                        </VideoMenuItem>
                        </template>
                      </div>
                    </template>
                  </VideoMenu>

                  <VideoMenu
                    ref="settingsMenuRef"
                    sheet
                    class="flex flex-col"
                  >
                    <template #default="{ toggle }">
                      <button
                        class="btn videoBtn"
                        @click="toggle()"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          xmlns:xlink="http://www.w3.org/1999/xlink"
                          aria-hidden="true"
                          role="img"
                          class="iconify iconify--ic"
                          width="24"
                          height="24"
                          preserveAspectRatio="xMidYMid meet"
                          viewBox="0 0 24 24"
                        >
                          <path
                            fill="currentColor"
                            d="M19.5 12c0-.23-.01-.45-.03-.68l1.86-1.41c.4-.3.51-.86.26-1.3l-1.87-3.23a.987.987 0 0 0-1.25-.42l-2.15.91c-.37-.26-.76-.49-1.17-.68l-.29-2.31c-.06-.5-.49-.88-.99-.88h-3.73c-.51 0-.94.38-1 .88l-.29 2.31c-.41.19-.8.42-1.17.68l-2.15-.91c-.46-.2-1-.02-1.25.42L2.41 8.62c-.25.44-.14.99.26 1.3l1.86 1.41a7.343 7.343 0 0 0 0 1.35l-1.86 1.41c-.4.3-.51.86-.26 1.3l1.87 3.23c.25.44.79.62 1.25.42l2.15-.91c.37.26.76.49 1.17.68l.29 2.31c.06.5.49.88.99.88h3.73c.5 0 .93-.38.99-.88l.29-2.31c.41-.19.8-.42 1.17-.68l2.15.91c.46.2 1 .02 1.25-.42l1.87-3.23c.25-.44.14-.99-.26-1.3l-1.86-1.41c.03-.23.04-.45.04-.68zm-7.46 3.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5s3.5 1.57 3.5 3.5s-1.57 3.5-3.5 3.5z"
                          ></path>
                        </svg>
                      </button>
                    </template>
                    <template #menu="{ close }">
                      <div
                        class="z-30 max-sm:z-[2147483004] flex flex-col gap-[10px] bg-neutral shadow-xl p-3 rounded overflow-y-auto overscroll-contain select-none max-sm:fixed max-sm:inset-x-2 max-sm:bottom-2 max-sm:max-h-[70vh] sm:absolute sm:right-0 sm:bottom-[52px] sm:max-h-[60vh] sm:w-max sm:min-w-[18rem] sm:max-w-[min(22rem,calc(100vw-1.5rem))]"
                      >
                        <VideoMenuItem
                          v-if="canClip"
                          @click="close(); openClipEditor()"
                        >
                          <div class="flex items-center gap-[6px]">
                            <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <circle cx="6" cy="6" r="3" />
                      <circle cx="6" cy="18" r="3" />
                      <path d="M20 4 8.12 15.88M14.47 14.48 20 20M8.12 8.12 12 12" />
                    </svg>
                            <span>Зробити кліп</span>
                          </div>
                        </VideoMenuItem>
                        <VideoMenuItem
                          v-if="anime.episodes[current].tgLink"
                          @click="openLink(anime.episodes[current].tgLink)"
                        >
                          <div class="flex items-center gap-[6px]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              xmlns:xlink="http://www.w3.org/1999/xlink"
                              aria-hidden="true"
                              role="img"
                              class="iconify iconify--ic"
                              width="24"
                              height="24"
                              preserveAspectRatio="xMidYMid meet"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="currentColor"
                                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19c-.14.75-.42 1-.68 1.03c-.58.05-1.02-.38-1.58-.75c-.88-.58-1.38-.94-2.23-1.5c-.99-.65-.35-1.01.22-1.59c.15-.15 2.71-2.48 2.76-2.69a.2.2 0 0 0-.05-.18c-.06-.05-.14-.03-.21-.02c-.09.02-1.49.95-4.22 2.79c-.4.27-.76.41-1.08.4c-.36-.01-1.04-.2-1.55-.37c-.63-.2-1.12-.31-1.08-.66c.02-.18.27-.36.74-.55c2.92-1.27 4.86-2.11 5.83-2.51c2.78-1.16 3.35-1.36 3.73-1.36c.08 0 .27.02.39.12c.1.08.13.19.14.27c-.01.06.01.24 0 .38z"
                              ></path>
                            </svg>
                            <span>{{ $t('telegramlink') }}</span>
                          </div>
                        </VideoMenuItem>
                        <VideoMenuItem
                          v-if="supportsPictureInPicture"
                          @click="
                            () => {
                              togglePictureInPicture()
                              close()
                            }
                          "
                        >
                          <div class="flex items-center gap-[6px]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              xmlns:xlink="http://www.w3.org/1999/xlink"
                              aria-hidden="true"
                              role="img"
                              class="iconify iconify--carbon"
                              width="24"
                              height="24"
                              preserveAspectRatio="xMidYMid meet"
                              viewBox="0 0 32 32"
                            >
                              <path
                                fill="currentColor"
                                d="M28 4H10a2.006 2.006 0 0 0-2 2v14a2.006 2.006 0 0 0 2 2h18a2.006 2.006 0 0 0 2-2V6a2.006 2.006 0 0 0-2-2Zm0 16H10V6h18Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M18 26H4V16h2v-2H4a2.006 2.006 0 0 0-2 2v10a2.006 2.006 0 0 0 2 2h14a2.006 2.006 0 0 0 2-2v-2h-2Z"
                              ></path>
                            </svg>
                            <span>{{ $t('pip') }}</span>
                          </div>
                        </VideoMenuItem>
                        <VideoMenuItem
                          v-if="castSupported"
                          @click="toggleCast"
                        >
                          <!-- Official Google Cast icon -->
                          <div class="flex items-center gap-[6px]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path
                                :fill="
                                  castActive ? 'currentColor' : 'currentColor'
                                "
                                :opacity="castActive ? 1 : 0.85"
                                d="M21 3H3c-1.1 0-2 .9-2 2v3h2V5h18v14h-7v2h7c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM1 18v3h3c0-1.66-1.34-3-3-3zm0-4v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7zm0-4v2c4.97 0 9 4.03 9 9h2c0-6.08-4.93-11-11-11z"
                              />
                              <!-- Active indicator dot -->
                              <circle
                                v-if="castActive"
                                cx="17"
                                cy="12"
                                r="3"
                                fill="currentColor"
                                class="text-primary"
                              />
                            </svg>
                            <span
                              :class="{
                                'text-primary': castActive,
                                'text-warning': castNoDevices
                              }"
                            >
                              {{
                                castActive
                                  ? 'Трансляція активна'
                                  : castNoDevices
                                    ? 'Пристроїв не знайдено'
                                    : 'Транслювати на ТВ'
                              }}
                            </span>
                          </div>
                        </VideoMenuItem>
                        <!-- <VideoMenuItem
                        @click="
                          openLink(
                            `https://cdn.olsior.tv/download?name=Олсіор дивиться аніме - ${encodeURI(
                              anime?.title.split('/ ')[1]
                            )} ${encodeURI(
                              anime?.episodes[current]?.title
                            )}&link=${encodeURI(
                              anime?.episodes[current]?.hls
                                .split('https://cdn.olsior.tv/')[1]
                                .split('/')
                            )
                              .split('.')
                              .join(']')}`
                          )
                        "
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          xmlns:xlink="http://www.w3.org/1999/xlink"
                          aria-hidden="true"
                          role="img"
                          class="iconify iconify--ph"
                          width="24"
                          height="24"
                          preserveAspectRatio="xMidYMid meet"
                          viewBox="0 0 256 256"
                        >
                          <path
                            fill="currentColor"
                            d="M71.5 88.5a12 12 0 0 1 17-17L116 99V24a12 12 0 0 1 24 0v75l27.5-27.5a12 12 0 0 1 17 17l-48 48a12.1 12.1 0 0 1-17 0ZM224 116h-39.4a12 12 0 0 0 0 24H220v56H36v-56h35.4a12 12 0 0 0 0-24H32a20.1 20.1 0 0 0-20 20v64a20.1 20.1 0 0 0 20 20h192a20.1 20.1 0 0 0 20-20v-64a20.1 20.1 0 0 0-20-20Zm-20 52a16 16 0 1 0-16 16a16 16 0 0 0 16-16Z"
                          ></path>
                        </svg>
                        <span>{{ $t('downloadVideo') }}</span>
                      </VideoMenuItem>
                      <VideoMenuItem>
                          <svg xmlns="http://www.w3.org/2000/svg"
                              xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true"
                              role="img" class="iconify iconify--bx" width="24" height="24"
                              preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24">
                              <path fill="currentColor"
                                  d="M20 3H4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zM4 9V5h16v4zm16 4H4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2zM4 19v-4h16v4z">
                              </path>
                              <path fill="currentColor"
                                  d="M17 6h2v2h-2zm-3 0h2v2h-2zm3 10h2v2h-2zm-3 0h2v2h-2z"></path>
                          </svg>
                          <span>autoplay</span>
                          <input class="toggle toggle-primary" type="checkbox"
                              @change="changeEpisode(current, true, currentTime)"
                              v-model="heroku">
                      </VideoMenuItem> -->
                        <VideoMenuItem v-if="anime.episodes.length > 1">
                          <div class="flex items-center gap-[6px]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="currentColor"
                                d="M4 18l8.5-6L4 6zm9-12v12l8.5-6z"
                              ></path>
                            </svg>
                            <span>Автоперемикання серії</span>
                          </div>
                          <input
                            class="toggle toggle-primary"
                            type="checkbox"
                            v-model="autoNextEpisode"
                          />
                        </VideoMenuItem>
                        <VideoMenuItem>
                          <div class="flex items-center gap-[6px]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="currentColor"
                                d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 15h-2v-6h2zm0-8h-2V7h2z"
                              ></path>
                            </svg>
                            <span>Дебаг інформація</span>
                          </div>
                          <input
                            class="toggle toggle-primary"
                            type="checkbox"
                            v-model="showDebugInfo"
                          />
                        </VideoMenuItem>
                        <VideoMenuItem>
                          <div class="flex items-center gap-[6px]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              xmlns:xlink="http://www.w3.org/1999/xlink"
                              aria-hidden="true"
                              role="img"
                              class="iconify iconify--carbon"
                              width="24"
                              height="24"
                              preserveAspectRatio="xMidYMid meet"
                              viewBox="0 0 32 32"
                            >
                              <path
                                fill="currentColor"
                                d="M26 16a9.928 9.928 0 0 0-1.14-4.618l-1.495 1.496A7.948 7.948 0 0 1 24 16zm-2.586-6L22 8.586L17.285 13.3A2.966 2.966 0 0 0 16 13a3 3 0 1 0 3 3a2.966 2.966 0 0 0-.3-1.285zM16 17a1 1 0 1 1 1-1a1.001 1.001 0 0 1-1 1zm0-9a7.952 7.952 0 0 1 3.122.635l1.496-1.496A9.986 9.986 0 0 0 6 16h2a8.01 8.01 0 0 1 8-8z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M16 30a14 14 0 1 1 14-14a14.016 14.016 0 0 1-14 14Zm0-26a12 12 0 1 0 12 12A12.014 12.014 0 0 0 16 4Z"
                              ></path>
                            </svg>
                            <span>{{ $t('speed') }}</span>
                          </div>
                          <select
                            v-model="controls.rate.value"
                            class="bg-base-300 bg-opacity-50 text-neutral-content select-sm select"
                            @change="changeRate"
                          >
                            <option value="2">2x</option>
                            <option value="1.75">1.75x</option>
                            <option value="1.5">1.5x</option>
                            <option value="1.25">1.25x</option>
                            <option value="1">1x</option>
                            <option value="0.75">0.75x</option>
                            <option value="0.5">0.5x</option>
                          </select>
                        </VideoMenuItem>
                        <VideoMenuItem v-if="qualityLevels.length > 1">
                          <div class="flex items-center gap-[6px]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="currentColor"
                                d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2m-8 12H9.5v-2h-2v2H6V9h1.5v2.5h2V9H11zm2-6h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4zm1.5 4.5h2v-3h-2z"
                              />
                            </svg>
                            <span>Якість</span>
                          </div>
                          <select
                            :value="qualityLevel"
                            class="bg-base-300 bg-opacity-50 text-neutral-content select-sm select"
                            @change="setQualityLevel($event.target.value)"
                          >
                            <option :value="-1">Авто</option>
                            <option
                              v-for="lvl in qualityLevels"
                              :key="lvl.index"
                              :value="lvl.index"
                            >
                              {{ lvl.label }}
                            </option>
                          </select>
                        </VideoMenuItem>
                        <!-- Кнопка завантаження епізоду -->
                        <VideoMenuItem
                          v-if="hlsCacheSupported && !isArchive"
                          @click="handleDownloadEpisode"
                        >
                          <!-- Іконка -->
                          <svg
                            v-if="!isDownloaded"
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                          >
                            <path
                              fill="currentColor"
                              d="M19 9h-4V3H9v6H5l7 7zm-8 2V5h2v6h1.17L12 13.17L9.83 11zm-6 8v-2h14v2z"
                            />
                          </svg>
                          <svg
                            v-else
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            class="text-success"
                          >
                            <path
                              fill="currentColor"
                              d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
                            />
                          </svg>

                          <!-- Текст + прогрес -->
                          <div class="flex flex-col flex-1 gap-0.5 min-w-0">
                            <span
                              v-if="isDownloaded"
                              class="font-medium text-success"
                              >Завантажено</span
                            >
                            <span v-else-if="isDownloading"
                              >Завантажується...</span
                            >
                            <template
                              v-else-if="episodeCacheInfo && !isDownloaded"
                            >
                              <span class="font-medium text-warning text-xs"
                                >Частково ({{ episodeCacheInfo.saved }}/{{
                                  episodeCacheInfo.total
                                }})</span
                              >
                              <div
                                class="bg-base-300 rounded-full w-full h-1.5 overflow-hidden"
                              >
                                <div
                                  class="bg-warning rounded-full h-full"
                                  :style="{
                                    width: `${Math.round((episodeCacheInfo.saved / episodeCacheInfo.total) * 100)}%`
                                  }"
                                ></div>
                              </div>
                            </template>
                            <span v-else>Завантажити офлайн</span>
                          </div>

                          <span
                            v-if="isDownloading"
                            class="loading loading-spinner loading-xs shrink-0"
                          ></span>
                        </VideoMenuItem>

                        <!-- Плейлист m3u8 для зовнішніх плеєрів -->
                        <VideoMenuItem v-if="currentPlaylistUrl && !isArchive">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                          >
                            <path
                              fill="currentColor"
                              d="M3 16h8v-2H3zm0-8h12V6H3zm0 4h12v-2H3zm14 6.5V8.9q.475.35 1.075.725T19.2 10.4q.85.5 1.325 1.075T21 13q0 1.05-.888 1.775T18 15.5q-.25 0-.5-.05t-.5-.125z"
                            />
                          </svg>

                          <div class="flex-1 min-w-0">
                            <span>Скачати для VLC</span>
                          </div>

                          <div class="flex items-center gap-1 shrink-0">
                            <!-- Копіювати посилання -->
                            <button
                              class="hover:bg-base-300/60 p-1 rounded transition-colors"
                              :title="
                                playlistCopied
                                  ? 'Скопійовано'
                                  : 'Копіювати посилання'
                              "
                              @click.stop="handleCopyPlaylistUrl"
                            >
                              <svg
                                v-if="!playlistCopied"
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  fill="currentColor"
                                  d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2m0 16H8V7h11z"
                                />
                              </svg>
                              <svg
                                v-else
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                class="text-success"
                              >
                                <path
                                  fill="currentColor"
                                  d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
                                />
                              </svg>
                            </button>

                            <!-- Зберегти файл -->
                            <button
                              class="hover:bg-base-300/60 p-1 rounded transition-colors"
                              title="Зберегти m3u8"
                              @click.stop="handleDownloadPlaylist"
                            >
                              <span
                                v-if="playlistSaving"
                                class="loading loading-spinner loading-xs"
                              ></span>
                              <svg
                                v-else
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  fill="currentColor"
                                  d="M19 9h-4V3H9v6H5l7 7zm-8 2V5h2v6h1.17L12 13.17L9.83 11zm-6 8v-2h14v2z"
                                />
                              </svg>
                            </button>
                          </div>
                        </VideoMenuItem>
                      </div>
                    </template>
                  </VideoMenu>
                  <button
                    class="btn videoBtn"
                    @click="toggleFullScreen"
                  >
                    <svg
                      v-if="!isFullscreen"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
                      ></path>
                    </svg>
                    <svg
                      v-else
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
                      ></path>
                    </svg>
                  </button>
                </div>
              </div>
          </client-only>
        </div>
      </div>
    </div>

    <ClientOnly>
      <LazyClipEditor
        v-if="clipOpen && canClip"
        id="clip-editor"
        :key="current"
        :get-video="getClipVideo"
        :get-fragments="getClipFragments"
        :source="clipSource"
        :thumb-source="thumbSource"
        @close="clipOpen = false"
      />
    </ClientOnly>

    <div
      v-if="anime.episodes.length > 1"
      class="mt-[10px]"
    >
      <div class="relative w-full">
        <button
          v-if="canScrollEpisodesUp"
          type="button"
          class="top-1 left-1/2 z-20 absolute flex justify-center items-center bg-base-300/80 hover:bg-base-300 shadow rounded-full outline-none focus:ring-2 focus:ring-primary w-8 h-8 -translate-x-1/2"
          @click="scrollEpisodes(-1)"
          aria-label="Scroll up"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
          >
            <path
              fill="currentColor"
              d="M7.41 15.41 12 10.83l4.59 4.58L18 14l-6-6-6 6z"
            ></path>
          </svg>
        </button>

        <div
          ref="episodesContainerRef"
          class="gap-[10px] grid grid-cols-2 lg:grid-cols-4 lg:grid-cols-6 w-full max-h-[420px] lg:max-h-[240px] overflow-y-auto episodes-container"
          @scroll="updateEpisodesScrollState"
        >
          <button
            v-for="(ep, ind) in anime.episodes"
            :key="ep?.title"
            class="relative shadow btn"
            :class="{
              'text-white bg-primary activeEpisode hover:bg-primary hover:border-primary':
                current == ind
            }"
            @click="changeEpisode(ind, true)"
            :title="ep?.title"
          >
            <!-- Лічильник живого онлайну на серії (лівий верхній кут) -->
            <span
              v-if="onlineStats.episodes?.[ind] > 0"
              class="top-1 left-1 z-20 absolute flex items-center gap-[3px] bg-neutral/80 shadow-lg px-[5px] py-[1px] rounded-md font-medium text-[11px] text-neutral-content leading-none"
            >
              <span class="relative flex w-[6px] h-[6px]">
                <span
                  class="inline-flex absolute bg-success opacity-75 rounded-full w-full h-full"
                ></span>
                <span
                  class="inline-flex relative bg-success rounded-full w-[6px] h-[6px]"
                ></span>
              </span>
              {{ onlineStats.episodes[ind] }}
            </span>

            <!-- Кількість коментарів до серії (лівий нижній кут) -->
            <span
              v-if="episodeCommentCount(ind) > 0"
              class="bottom-1 left-1 z-20 absolute flex items-center gap-[3px] bg-neutral/80 shadow-lg px-[5px] py-[1px] rounded-md font-medium text-[11px] text-neutral-content leading-none"
              :title="`Коментарів: ${episodeCommentCount(ind)}`"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="10"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2"
                />
              </svg>
              {{ episodeCommentCount(ind) }}
            </span>

            <!-- Іконка кешу (правий верхній кут) з tooltip -->
            <span
              v-if="allEpisodesCache[ind]"
              class="group top-1 right-1 z-20 absolute font-bold text-lg leading-none"
              :class="
                allEpisodesCache[ind].isComplete
                  ? 'text-success'
                  : 'text-warning'
              "
            >
              <!-- Зелена галочка для повністю завантажених -->
              <span
                v-if="allEpisodesCache[ind].isComplete"
                class="drop-shadow-md"
                >✓</span
              >
              <!-- Жовтий знак попередження для частково завантажених -->
              <span
                v-else
                class="drop-shadow-md"
                >⚠</span
              >
              <!-- Custom tooltip (вниз) -->
              <span
                class="invisible group-hover:visible top-full right-0 z-30 absolute bg-neutral opacity-0 group-hover:opacity-100 shadow-lg mt-2 px-2 py-1 rounded font-normal text-neutral-content text-xs whitespace-nowrap transition-opacity pointer-events-none"
              >
                {{
                  allEpisodesCache[ind].isComplete
                    ? 'Повністю завантажено'
                    : `Частково: ${allEpisodesCache[ind].saved}/${allEpisodesCache[ind].total} сегм.`
                }}
                <span
                  class="right-2 bottom-full absolute border-4 border-transparent border-b-neutral"
                ></span>
              </span>
            </span>

            <div
              class="text-shadow overflow-hidden text-sm text-left text-ellipsis whitespace-nowrap"
            >
              {{ ep?.title }}
            </div>
          </button>
        </div>

        <button
          v-if="canScrollEpisodesDown"
          type="button"
          class="bottom-1 left-1/2 z-20 absolute flex justify-center items-center bg-base-300/80 hover:bg-base-300 shadow rounded-full outline-none focus:ring-2 focus:ring-primary w-8 h-8 -translate-x-1/2"
          @click="scrollEpisodes(1)"
          aria-label="Scroll down"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
          >
            <path
              fill="currentColor"
              d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z"
            ></path>
          </svg>
        </button>
      </div>
    </div>

    <!-- Гілка коментарів своя на кожну серію; для фільмів в одну серію
         вистачає спільної гілки аніме внизу сторінки -->
    <LazyWatchComments
      v-if="anime.episodes.length > 1 && !isFullscreen"
      :page-id="episodeCommentsPageId"
      :title="$t('commentsep')"
      :page-title="episodeCommentsTitle"
      :page-url="episodeCommentsUrl"
      :anime="String(route.params.name)"
      :episode="current + 1"
    />
  </div>
</template>

<style>
.showModal {
  animation: showModal 0.25s ease;
}

@keyframes showModal {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-50%) scale(0.9);
  }

  to {
    opacity: 1;
    transform: translateX(-50%) translateY(-50%) scale(1);
  }
}

.tooltip::before {
  font-size: inherit;
  padding: inherit;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.5);
}
</style>
